import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { transformationsService, Transformation, TransformationMember } from '@/services/transformations';
import { Button } from '@/components/ui/Button';
import toast from 'react-hot-toast';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { useTransformationPermissions } from '@/hooks/useTransformationPermissions';
import api from '@/services/api';
import { ContextualPanel, ContextualPanelItem } from '@/components/common/ContextualPanel';
import { 
  PlusIcon, 
  ClockIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import {
  InchstoneHierarchy,
  Inchstone
} from '@/components/inchstones/InchstoneHierarchy';
import { Template as ProjectStructureTemplate } from '@/services/templateService';
import { actionService } from '@/services/actionService';
import { PositionStakeholderList, PositionStakeholderSelector } from '@/components/position-stakeholder';
import { positionStakeholderService } from '@/services/positionStakeholderService';
import { TransformationForm } from './TransformationForm';
import { ProjectModeToggle } from '@/components/transformations/ProjectModeToggle';
import { useOperationsPane } from '@/components/operations/operationsContext';

interface InchstoneStats {
  total_inchstones: number;
  completed_inchstones: number;
  completion_percentage: number;
  overdue_inchstones: number;
}

// Option A: Project team is managed via Members (transformation_members). Only external/role-based stakeholders here.
const STAKEHOLDER_ROLES = [
  { value: 'sponsors', label: 'Sponsors' },
  { value: 'customers', label: 'Customers' },
  { value: 'employees', label: 'Employees' },
  { value: 'suppliers', label: 'Suppliers' },
  { value: 'regulators', label: 'Regulators' },
] as const;

export const TransformationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, translateWithFallback } = useLanguage();
  const { user: currentUser } = useAuth();
  const { isReadOnly: permissionBasedReadOnly } = useTransformationPermissions();
  const { mutationEpoch } = useOperationsPane();
  const lastMutationEpoch = useRef(mutationEpoch);

  const [transformation, setTransformation] = useState<Transformation | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessDeniedReason, setAccessDeniedReason] = useState<string>('');
  
  // Inchstone state
  const [inchstones, setInchstones] = useState<Inchstone[]>([]);
  const [inchstoneStats, setInchstoneStats] = useState<InchstoneStats>({
    total_inchstones: 0,
    completed_inchstones: 0,
    completion_percentage: 0,
    overdue_inchstones: 0
  });
  const [inchstoneLoading, setInchstoneLoading] = useState(false);
  const [inchstoneError, setInchstoneError] = useState<string | null>(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [projectStructureTemplates, setProjectStructureTemplates] = useState<ProjectStructureTemplate[]>([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [creatingWithAI, setCreatingWithAI] = useState(false);
  
  // Risks and Issues state
  const [risks, setRisks] = useState<any[]>([]);
  const [risksLoading, setRisksLoading] = useState(false);
  const [issues, setIssues] = useState<any[]>([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [kpis, setKpis] = useState<any[]>([]);
  const [kpisLoading, setKpisLoading] = useState(false);
  const [comments, setComments] = useState<any[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [projectHistory, setProjectHistory] = useState<any[]>([]);
  const [projectHistoryLoading, setProjectHistoryLoading] = useState(false);
  const [actions, setActions] = useState<any[]>([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  
  // Contextual Panel state
  const [activePanel, setActivePanel] = useState<'risks' | 'kpis' | 'issues' | 'stakeholders' | 'discussions' | 'actions' | 'settings' | 'history' | null>(null);
  const [showCreateForm, setShowCreateForm] = useState<'risks' | 'issues' | 'discussions' | 'actions' | null>(null);
  
  // Create form states (no titles - project context is the title)
  const [createActionForm, setCreateActionForm] = useState({ description: '', priority: 'MEDIUM' as 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL', due_date: '' });
  const [createActionAssignedUserIds, setCreateActionAssignedUserIds] = useState<string[]>([]);
  const [createActionUserSearchTerm, setCreateActionUserSearchTerm] = useState('');
  const [createActionUserSearchResults, setCreateActionUserSearchResults] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [createActionAllUsers, setCreateActionAllUsers] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [createActionUserSearchLoading, setCreateActionUserSearchLoading] = useState(false);
  const [showCreateActionUserSearch, setShowCreateActionUserSearch] = useState(false);
  const createActionUserSearchRef = useRef<HTMLDivElement>(null);
  const [createIssueForm, setCreateIssueForm] = useState({ description: '', urgency: 'MEDIUM' as 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL', impact: 'MEDIUM' as 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' });
  const [createRiskForm, setCreateRiskForm] = useState({ name: '', description: '', severity: 'MEDIUM' as 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL', probability: 'MEDIUM' as 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' });
  const [createDiscussionForm, setCreateDiscussionForm] = useState({ content: '' });
  
  // Stakeholders state (transformation-level; position stakeholders hidden for now)
  const [stakeholders, setStakeholders] = useState<any[]>([]);
  const [stakeholdersLoading, setStakeholdersLoading] = useState(false);
  // Phase 2: Transformation members
  const [members, setMembers] = useState<TransformationMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [addMemberSearch, setAddMemberSearch] = useState('');
  const [addMemberSearchResults, setAddMemberSearchResults] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [addMemberSearchLoading, setAddMemberSearchLoading] = useState(false);
  const [addMemberSubmitting, setAddMemberSubmitting] = useState(false);
  const [addMemberRole, setAddMemberRole] = useState<string>('');
  const [showAddMemberDropdown, setShowAddMemberDropdown] = useState(false);
  const addMemberSearchRef = useRef<HTMLDivElement>(null);
  const [addStakeholderInput, setAddStakeholderInput] = useState('');
  const [addStakeholderSubmitting, setAddStakeholderSubmitting] = useState(false);
  const [positionStakeholders, setPositionStakeholders] = useState<any[]>([]);
  const SHOW_POSITION_STAKEHOLDERS = false; // set true to show position-based stakeholders again

  // Only project members (owner, manager, or in members list) can edit
  const isProjectMember = useMemo(() => {
    if (!currentUser?.id || !transformation) return false;
    if (transformation.owner_id === currentUser.id || transformation.manager_id === currentUser.id) return true;
    if (membersLoading) return false;
    return members.some((m) => m.user_id === currentUser.id);
  }, [currentUser?.id, transformation?.owner_id, transformation?.manager_id, transformation, members, membersLoading]);

  // Check if viewMode is set in navigation state (explicit view action)
  const viewMode = (location.state as any)?.viewMode === true;

  // Read-only if: explicit view mode, no RBAC update, or not a project member
  const isReadOnly = viewMode || permissionBasedReadOnly || !isProjectMember;
  
  // Stakeholder search/autocomplete state
  const [stakeholderSearchResults, setStakeholderSearchResults] = useState<Array<{ id?: string; name: string; email?: string; type: 'stakeholder' | 'user' | 'new' }>>([]);
  const [stakeholderSearchLoading, setStakeholderSearchLoading] = useState(false);
  const [showStakeholderDropdown, setShowStakeholderDropdown] = useState(false);
  const stakeholderSearchRef = useRef<HTMLDivElement>(null);
  const [addStakeholderRole, setAddStakeholderRole] = useState<string>('sponsors');
  const [dragOverBucket, setDragOverBucket] = useState<string | null>(null);
  const [draggingStakeholderId, setDraggingStakeholderId] = useState<string | null>(null);
  
  // Manager state
  const [manager, setManager] = useState<{ id: string; name: string; email?: string } | null>(null);
  const [managerLoading, setManagerLoading] = useState(false);
  
  // Owner state
  const [owner, setOwner] = useState<{ id: string; name: string; email?: string } | null>(null);
  const [ownerLoading, setOwnerLoading] = useState(false);
  
  // Manager assignment state
  const [showManagerModal, setShowManagerModal] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<any[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const userSearchRef = useRef<HTMLDivElement>(null);
  // Owner assignment (in panel)
  const [showOwnerPicker, setShowOwnerPicker] = useState(false);
  const [ownerSearchTerm, setOwnerSearchTerm] = useState('');
  const [ownerSearchResults, setOwnerSearchResults] = useState<any[]>([]);
  const [ownerSearchLoading, setOwnerSearchLoading] = useState(false);
  /** Anchor rect for positioning owner/manager popups locally (from trigger button) */
  const [pickerAnchor, setPickerAnchor] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const [ownerUpdateInProgress, setOwnerUpdateInProgress] = useState(false);
  const [managerUpdateInProgress, setManagerUpdateInProgress] = useState(false);
  
  // Project Status state
  const [projectStatus, setProjectStatus] = useState({
    overall_summary: '',
    cost_summary: '',
    benefits_summary: '',
    execution_health_summary: '',
    overall_status: 'GREEN' as 'RED' | 'YELLOW' | 'GREEN',
    cost_status: 'GREEN' as 'RED' | 'YELLOW' | 'GREEN',
    benefits_status: 'GREEN' as 'RED' | 'YELLOW' | 'GREEN',
    execution_health_status: 'GREEN' as 'RED' | 'YELLOW' | 'GREEN',
    next_steps: '',
    asks: ''
  });
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedStatusRef = useRef(projectStatus);

  const applyProjectStatusRow = (latestStatus: Record<string, any> | null | undefined) => {
    if (!latestStatus) return;
    const textOrPrev = (incoming: unknown, prev: string) =>
      incoming == null || String(incoming).trim() === '' ? prev : String(incoming);
    const ragOrPrev = (incoming: unknown, prev: 'RED' | 'YELLOW' | 'GREEN'): 'RED' | 'YELLOW' | 'GREEN' => {
      const value = typeof incoming === 'string' ? incoming.toUpperCase() : '';
      return value === 'RED' || value === 'YELLOW' || value === 'GREEN' ? value : prev;
    };
    setProjectStatus((prev) => {
      const next = {
        overall_summary: textOrPrev(latestStatus['overall_summary'], prev.overall_summary),
        cost_summary: textOrPrev(latestStatus['cost_summary'], prev.cost_summary),
        benefits_summary: textOrPrev(latestStatus['benefits_summary'], prev.benefits_summary),
        execution_health_summary: textOrPrev(
          latestStatus['execution_health_summary'],
          prev.execution_health_summary
        ),
        overall_status: ragOrPrev(latestStatus['overall_status'], prev.overall_status),
        cost_status: ragOrPrev(latestStatus['cost_status'], prev.cost_status),
        benefits_status: ragOrPrev(latestStatus['benefits_status'], prev.benefits_status),
        execution_health_status: ragOrPrev(
          latestStatus['execution_health_status'],
          prev.execution_health_status
        ),
        next_steps: textOrPrev(latestStatus['next_steps'], prev.next_steps),
        asks: textOrPrev(latestStatus['asks'], prev.asks)
      };
      lastSavedStatusRef.current = next;
      return next;
    });
  };

  // Helper function to determine if an error is actually an error
  // Only treat as error if:
  // 1. Valid response that explicitly says it's an error (500+ status)
  // 2. Communication problem (no response, network error, timeout)
  // Otherwise (404, 400, empty data) → NOT an error
  const isActualError = (error: any): boolean => {
    // Timeout = communication problem = error
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || 
        (error.message && error.message.toLowerCase().includes('timeout'))) {
      return true;
    }
    // No response = communication problem = error
    if (!error.response) {
      return true;
    }
    // 500+ status = server error = error
    if (error.response.status >= 500) {
      return true;
    }
    // 404, 400, etc. = data doesn't exist or invalid request = NOT an error
    return false;
  };

  // Load transformation and related data
  // Note: READ permission is already checked by ProtectedRBACRoute, so we don't need to check again
  // The backend will filter by hierarchy scope - if transformation is not accessible, it will return 404/403
  useEffect(() => {
    if (id) {
      loadTransformation();
      loadInchstones();
      loadInchstoneStats();
      loadRisks();
      loadIssues();
      loadKPIs();
      loadStakeholders();
      loadMembers();
      if (SHOW_POSITION_STAKEHOLDERS) loadPositionStakeholders();
      loadComments();
      loadProjectHistory();
      loadActions();
    }
  }, [id]);

  useEffect(() => {
    if (transformation?.manager_id) {
      loadManager();
    } else {
      setManager(null);
    }
  }, [transformation?.manager_id]);

  useEffect(() => {
    if (transformation?.owner_id) {
      loadOwner();
    } else {
      setOwner(null);
    }
  }, [transformation?.owner_id]);

  // Handle click outside user search dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userSearchRef.current && !userSearchRef.current.contains(event.target as Node)) {
        setShowManagerModal(false);
        setPickerAnchor(null);
        setUserSearchTerm('');
        setUserSearchResults([]);
      }
    };

    if (showManagerModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showManagerModal]);

  // Handle click outside stakeholder search dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (stakeholderSearchRef.current && !stakeholderSearchRef.current.contains(event.target as Node)) {
        setShowStakeholderDropdown(false);
      }
    };

    if (showStakeholderDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStakeholderDropdown]);

  // Handle click outside add-member search dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addMemberSearchRef.current && !addMemberSearchRef.current.contains(event.target as Node)) {
        setShowAddMemberDropdown(false);
      }
    };
    if (showAddMemberDropdown) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAddMemberDropdown]);

  // Debounced search for add member (tenant users, via generic users API)
  useEffect(() => {
    const query = addMemberSearch.trim();
    if (query.length < 2) {
      setAddMemberSearchResults([]);
      setShowAddMemberDropdown(false);
      return;
    }
    const timeoutId = setTimeout(async () => {
      try {
        setAddMemberSearchLoading(true);
        const response = await api.get('/generic/users');
        const list: any[] = response.data?.data ?? [];
        const existingIds = new Set(members.map((m) => m.user_id));
        const filtered = list
          .filter((u: any) =>
            u.id &&
            !existingIds.has(u.id) &&
            (
              (u.first_name && u.first_name.toLowerCase().includes(query.toLowerCase())) ||
              (u.last_name && u.last_name.toLowerCase().includes(query.toLowerCase())) ||
              (u.email && u.email.toLowerCase().includes(query.toLowerCase()))
            )
          )
          .map((u: any) => ({
            id: u.id,
            name: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || u.id,
            email: u.email || ''
          }));
        setAddMemberSearchResults(filtered);
        setShowAddMemberDropdown(true);
      } catch {
        setAddMemberSearchResults([]);
      } finally {
        setAddMemberSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [addMemberSearch, members]);

  // Debounced search for stakeholders and users
  useEffect(() => {
    const query = addStakeholderInput.trim();
    if (query.length < 2) {
      setStakeholderSearchResults([]);
      setShowStakeholderDropdown(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      searchStakeholdersAndUsers(query);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [addStakeholderInput, currentUser?.tenantId]);

  const loadTransformation = async () => {
    try {
      setLoading(true);
      console.log('[TransformationDetailPage] Loading transformation:', id);
      const data = await transformationsService.getTransformation(id!);
      console.log('[TransformationDetailPage] Transformation loaded:', data?.id);
      setTransformation(data);
      
      // Reset initial load flag when loading new transformation
      isInitialLoad.current = true;
      
      // Load project status if available (optional - no error if missing)
      try {
        const statusResponse = await api.get(`/transformations/${id}/project-status`);
        if (statusResponse.data?.success && statusResponse.data.data) {
          const latestStatus = Array.isArray(statusResponse.data.data) 
            ? statusResponse.data.data[0] // Get the latest entry if it's an array
            : statusResponse.data.data;
          applyProjectStatusRow(latestStatus);
        }
      } catch (statusError: any) {
        // Only treat as error if it's a communication problem or server error
        if (isActualError(statusError)) {
          console.error('Error loading project status:', statusError);
        }
        // Silently continue with default values - missing data is not an error
      }
      console.log('[TransformationDetailPage] Setting loading to false (success)');
      setLoading(false); // Always set loading to false on success
    } catch (err: any) {
      console.error('[TransformationDetailPage] Error loading transformation:', err);
      // Phase 5.4: Clear 403 and 404 messaging
      if (err.response?.status === 403 || err.response?.status === 404) {
        setAccessDenied(true);
        const errorCode = err.response?.data?.errorCode;
        const msg = err.response?.data?.message;
        if (err.response?.status === 403 && errorCode === 'INSUFFICIENT_CLEARANCE') {
          setAccessDeniedReason('Your clearance is insufficient to view this resource. Contact your administrator for higher clearance.');
        } else if (err.response?.status === 404) {
          setAccessDeniedReason("You don't have access to this resource. Contact the project lead or your administrator.");
        } else if (msg && typeof msg === 'string') {
          setAccessDeniedReason(msg);
        } else {
          setAccessDeniedReason('This transformation is not within your scope or does not exist. Contact the project lead or your administrator.');
        }
      } else {
        toast.error(t('transformation.failedToLoadTransformation'));
        navigate('/transformations');
      }
      console.log('[TransformationDetailPage] Setting loading to false (error)');
      setLoading(false);
    }
  };


  const loadInchstones = async () => {
    try {
      setInchstoneLoading(true);
      const response = await api.get(`/inchstones/transformations/${id}/inchstones`);
      
      if (response.data.success) {
        
        // Always build hierarchy from flat data to ensure proper structure
        let hierarchicalInchstones = [];
        if (response.data.data.flat_inchstones && response.data.data.flat_inchstones.length > 0) {
          hierarchicalInchstones = buildHierarchyFromFlat(response.data.data.flat_inchstones);
        } else if (response.data.data.inchstones && response.data.data.inchstones.length > 0) {
          hierarchicalInchstones = response.data.data.inchstones;
        }
        
        setInchstones(hierarchicalInchstones);
      }
    } catch (err: any) {
      // Only treat as error if it's a communication problem or server error
      if (isActualError(err)) {
        console.error('Error loading inchstones:', err);
        setInchstoneError(t('transformation.failedToFetchInchstones'));
      }
      // Silently set empty array - missing data is not an error
      setInchstones([]);
    } finally {
      setInchstoneLoading(false);
    }
  };

  // Helper function to build hierarchy from flat data
  const buildHierarchyFromFlat = (flatInchstones: Inchstone[]): Inchstone[] => {
    
    const inchstoneMap = new Map<string, Inchstone & { children: Inchstone[] }>();
    const rootInchstones: (Inchstone & { children: Inchstone[] })[] = [];

    // Create map for quick lookup
    flatInchstones.forEach((inchstone: Inchstone) => {
      inchstoneMap.set(inchstone.id, { ...inchstone, children: [] });
    });

    // Build hierarchy
    flatInchstones.forEach((inchstone: Inchstone) => {
      const inchstoneWithChildren = inchstoneMap.get(inchstone.id);
      if (inchstone.parent_id) {
        const parent = inchstoneMap.get(inchstone.parent_id);
        if (parent) {
          parent.children.push(inchstoneWithChildren!);
        }
      } else {
        rootInchstones.push(inchstoneWithChildren!);
      }
    });

    // Sort children by sort_order
    const sortChildren = (inchstones: (Inchstone & { children: Inchstone[] })[]) => {
      inchstones.sort((a, b) => a.sort_order - b.sort_order);
      inchstones.forEach(inchstone => {
        if (inchstone.children.length > 0) {
          sortChildren(inchstone.children as (Inchstone & { children: Inchstone[] })[]);
        }
      });
    };

    sortChildren(rootInchstones);
    return rootInchstones;
  };

  const loadInchstoneStats = async () => {
    try {
      const response = await api.get(`/inchstones/transformations/${id}/inchstones/stats`);
      if (response.data?.success) {
        setInchstoneStats(response.data.data);
      } else {
        // Empty or unsuccessful response is normal - no stats exist yet
        setInchstoneStats({
          total_inchstones: 0,
          completed_inchstones: 0,
          completion_percentage: 0,
          overdue_inchstones: 0
        });
      }
    } catch (err: any) {
      // Only treat as error if it's a communication problem or server error
      if (isActualError(err)) {
        console.error('Error loading inchstone stats:', err);
      }
      // Set default stats - missing data is not an error
      setInchstoneStats({
        total_inchstones: 0,
        completed_inchstones: 0,
        completion_percentage: 0,
        overdue_inchstones: 0
      });
    }
  };

  const loadProjectStructureTemplates = async () => {
    try {
      setTemplateLoading(true);
      setTemplateError(null);
      // Load templates from marketplace (PUBLIC and PRIVATE, excluding DRAFT)
      // PROJECT_STRUCTURE template type IS the project/program/transformation template type
      const response = await api.get('/templates/marketplace', {
        params: { template_type: 'PROJECT_STRUCTURE' }
      });
      
      if (response.data.success) {
        const data = response.data.data;
        // Handle both response formats: { templates: [...] } or direct array
        const templates = data?.templates || (Array.isArray(data) ? data : []);
        setProjectStructureTemplates(templates);
      } else {
        throw new Error(response.data.error || 'Failed to load templates');
      }
    } catch (error) {
      console.error('Error loading project structure templates:', error);
      setTemplateError(
        t('transformation.failedToLoadProjectStructureTemplates') ||
          'Failed to load project structure templates'
      );
    } finally {
      setTemplateLoading(false);
    }
  };

  const openTemplateModal = () => {
    setTemplateModalOpen(true);
    loadProjectStructureTemplates();
  };

  const closeTemplateModal = () => {
    setTemplateModalOpen(false);
    setSelectedTemplateId('');
  };

  const handleApplyProjectStructureTemplate = async () => {
    if (!selectedTemplateId) {
      toast.error(
        t('transformation.selectProjectStructureTemplate') || 'Please select a project structure template'
      );
      return;
    }

    try {
      setApplyingTemplate(true);
      await api.post(`/transformations/${id}/apply-project-structure-template`, {
        template_id: selectedTemplateId
      });
      toast.success(
        t('transformation.projectStructureTemplateApplied') || 'Project structure template applied'
      );
      closeTemplateModal();
      await loadInchstones();
      await loadInchstoneStats();
    } catch (error) {
      console.error('Error applying project structure template:', error);
      toast.error(
        t('transformation.projectStructureTemplateApplyFailed') || 'Failed to apply project structure template'
      );
    } finally {
      setApplyingTemplate(false);
    }
  };

  const handleCreateInchstonesWithAI = async () => {
    try {
      setCreatingWithAI(true);
      const response = await api.post(`/transformations/${id}/create-inchstones-with-ai`);
      if (response.data?.success) {
        const count = response.data?.data?.created_inchstones ?? 0;
        toast.success(
          t('transformation.inchstonesCreatedWithAI', { count }) || `Created ${count} inchstone(s) with AI`
        );
        await loadInchstones();
        await loadInchstoneStats();
      } else {
        toast.error(response.data?.error || t('transformation.createWithAIFailed') || 'Failed to create inchstones with AI');
      }
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.message || 'Failed to create inchstones with AI';
      toast.error(msg);
    } finally {
      setCreatingWithAI(false);
    }
  };

  const loadRisks = async () => {
    try {
      setRisksLoading(true);
      const response = await api.get(`/transformations/${id}/risks`);
      if (response.data?.success) {
        // Filter out closed risks
        const filteredRisks = (response.data.data || []).filter((risk: any) => {
          const status = risk.risk_status || risk.status;
          return status !== 'CLOSED' && status !== 'RESOLVED';
        });
        // Sort by due_date (if available) or created_at (latest first)
        filteredRisks.sort((a: any, b: any) => {
          const aDate = a.due_date ? new Date(a.due_date).getTime() : (a.created_at ? new Date(a.created_at).getTime() : 0);
          const bDate = b.due_date ? new Date(b.due_date).getTime() : (b.created_at ? new Date(b.created_at).getTime() : 0);
          return aDate - bDate; // Ascending order (earliest first)
        });
        setRisks(filteredRisks);
      } else {
        // Empty or unsuccessful response is normal - no risks exist yet
        setRisks([]);
      }
    } catch (error: any) {
      // Only treat as error if it's a communication problem or server error
      if (isActualError(error)) {
        console.error('Error loading risks:', error);
      }
      // Silently set empty array - missing data is not an error
      setRisks([]);
    } finally {
      setRisksLoading(false);
    }
  };

  const loadIssues = async () => {
    try {
      setIssuesLoading(true);
      const response = await api.get(`/transformations/${id}/issues`);
      if (response.data?.success) {
        // Filter out closed issues
        const filteredIssues = (response.data.data || []).filter((issue: any) => issue.status !== 'CLOSED');
        // Sort by due_date (if available) or created_at (latest first)
        filteredIssues.sort((a: any, b: any) => {
          const aDate = a.due_date ? new Date(a.due_date).getTime() : (a.created_at ? new Date(a.created_at).getTime() : 0);
          const bDate = b.due_date ? new Date(b.due_date).getTime() : (b.created_at ? new Date(b.created_at).getTime() : 0);
          return aDate - bDate; // Ascending order (earliest first)
        });
        setIssues(filteredIssues);
      } else {
        // Empty or unsuccessful response is normal - no issues exist yet
        setIssues([]);
      }
    } catch (error: any) {
      // Only treat as error if it's a communication problem or server error
      if (isActualError(error)) {
        console.error('Error loading issues:', error);
      }
      // Silently set empty array - missing data is not an error
      setIssues([]);
    } finally {
      setIssuesLoading(false);
    }
  };

  const loadKPIs = async () => {
    try {
      setKpisLoading(true);
      const response = await api.get(`/transformations/${id}/kpis`);
      if (response.data?.success) {
        setKpis(response.data.data || []);
      } else {
        // Empty or unsuccessful response is normal - no KPIs exist yet
        setKpis([]);
      }
    } catch (error: any) {
      // Only treat as error if it's a communication problem or server error
      if (isActualError(error)) {
        console.error('Error loading KPIs:', error);
      }
      // Silently set empty array - missing data is not an error
      setKpis([]);
    } finally {
      setKpisLoading(false);
    }
  };

  const loadStakeholders = async () => {
    try {
      setStakeholdersLoading(true);
      const response = await api.get(`/transformations/${id}/stakeholders`);
      if (response.data?.success) {
        setStakeholders(response.data.data || []);
      } else {
        // Empty or unsuccessful response is normal - no stakeholders exist yet
        setStakeholders([]);
      }
    } catch (error: any) {
      // Only treat as error if it's a communication problem or server error
      if (isActualError(error)) {
        console.error('Error loading stakeholders:', error);
      }
      // Silently set empty array - missing data is not an error
      setStakeholders([]);
    } finally {
      setStakeholdersLoading(false);
    }
  };

  const loadMembers = async () => {
    if (!id) return;
    try {
      setMembersLoading(true);
      const list = await transformationsService.getMembers(id);
      setMembers(list);
    } catch (error: any) {
      if (isActualError(error)) console.error('Error loading members:', error);
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const loadPositionStakeholders = async () => {
    if (!id) return;
    try {
      const list = await positionStakeholderService.getPositionStakeholders('TRANSFORMATION', id);
      setPositionStakeholders(list);
    } catch (error: any) {
      console.error('Error loading position stakeholders:', error);
      setPositionStakeholders([]);
    }
  };

  const searchStakeholdersAndUsers = async (query: string) => {
    if (!currentUser?.tenantId) return;
    try {
      setStakeholderSearchLoading(true);
      const queryLower = query.toLowerCase().trim();
      const [stakeholdersRes, usersRes] = await Promise.all([
        api.get('/stakeholders', { params: { search: query } }).catch(() => ({ data: { data: [] } })),
        api.get('/users/admin/users', { params: { search: query, limit: 10 } }).catch(() => ({ data: { data: { users: [] } } }))
      ]);
      
      const existingStakeholderIds = new Set(stakeholders.map(s => s.id));
      const results: Array<{ id?: string; name: string; email?: string; type: 'stakeholder' | 'user' | 'new' }> = [];
      
      // Add existing stakeholders (exclude already assigned) - filter to ensure they actually match
      const foundStakeholders = stakeholdersRes.data?.data || [];
      foundStakeholders.forEach((sh: any) => {
        if (!existingStakeholderIds.has(sh.id)) {
          const nameMatch = sh.name?.toLowerCase().includes(queryLower);
          const emailMatch = sh.email?.toLowerCase().includes(queryLower);
          if (nameMatch || emailMatch) {
            results.push({
              id: sh.id,
              name: sh.name,
              email: sh.email,
              type: 'stakeholder'
            });
          }
        }
      });
      
      // Add users (as potential new stakeholders) - filter to ensure they actually match
      const users = usersRes.data?.data?.users || usersRes.data?.data || [];
      users.forEach((u: any) => {
        if (!currentUser?.tenantId || u.tenant_id === currentUser.tenantId) {
          const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
          const nameMatch = name.toLowerCase().includes(queryLower);
          const emailMatch = u.email?.toLowerCase().includes(queryLower);
          if (nameMatch || emailMatch) {
            results.push({
              id: u.id,
              name,
              email: u.email,
              type: 'user'
            });
          }
        }
      });
      
      // Only add "new" option if there are NO matches at all (not just no exact match)
      // This allows creating new stakeholders when typing something that doesn't exist
      if (results.length === 0 && query.length >= 2) {
        const isEmail = query.includes('@');
        results.push({
          name: isEmail ? query.split('@')[0].replace(/\./g, ' ').trim() || query : query,
          email: isEmail ? query : undefined,
          type: 'new'
        });
      }
      
      setStakeholderSearchResults(results.slice(0, 10));
      setShowStakeholderDropdown(results.length > 0);
    } catch (error: any) {
      console.error('Error searching stakeholders/users:', error);
      setStakeholderSearchResults([]);
      setShowStakeholderDropdown(false);
    } finally {
      setStakeholderSearchLoading(false);
    }
  };

  const handleAddStakeholder = async (input: string | { id?: string; name: string; email?: string; type: 'stakeholder' | 'user' | 'new' }) => {
    if (addStakeholderSubmitting || !id) return;
    setAddStakeholderSubmitting(true);
    try {
      let stakeholderId: string | undefined;
      
      if (typeof input === 'string') {
        // Manual entry - create new stakeholder
        const raw = input.trim();
        const isEmail = raw.includes('@');
        const name = isEmail ? raw.split('@')[0].replace(/\./g, ' ').trim() || raw : raw;
        const email = isEmail ? raw : undefined;
        const createRes = await api.post('/stakeholders', {
          name: name || raw,
          email: email || undefined,
          is_external: true
        });
        stakeholderId = createRes.data?.data?.id;
      } else {
        // Selected from dropdown
        if (input.type === 'stakeholder' && input.id) {
          // Existing stakeholder - just assign
          stakeholderId = input.id;
        } else if (input.type === 'user' && input.id) {
          // User - create stakeholder linked to user
          const createRes = await api.post('/stakeholders', {
            name: input.name,
            email: input.email,
            user_id: input.id,
            is_external: false
          });
          stakeholderId = createRes.data?.data?.id;
        } else {
          // New - create stakeholder
          const createRes = await api.post('/stakeholders', {
            name: input.name,
            email: input.email,
            is_external: true
          });
          stakeholderId = createRes.data?.data?.id;
        }
      }
      
      if (stakeholderId) {
        await api.post('/stakeholder-assignments', {
          stakeholder_id: stakeholderId,
          entity_type: 'project',
          entity_id: id,
          association_type: addStakeholderRole
        });
        setAddStakeholderInput('');
        setShowStakeholderDropdown(false);
        setStakeholderSearchResults([]);
        await loadStakeholders();
        toast.success('Stakeholder added');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to add stakeholder');
    } finally {
      setAddStakeholderSubmitting(false);
    }
  };

  const handleRemoveStakeholder = async (stakeholderId: string) => {
    if (!id || !stakeholderId) return;
    if (!confirm('Are you sure you want to remove this stakeholder from this transformation?')) return;
    try {
      await api.delete(`/stakeholder-assignments/${stakeholderId}/project/${id}`);
      await loadStakeholders();
      toast.success('Stakeholder removed');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to remove stakeholder');
    }
  };

  const handleChangeStakeholderRole = async (stakeholderId: string, newRole: string) => {
    if (!id || !stakeholderId) return;
    try {
      await api.put(`/stakeholder-assignments/${stakeholderId}/project/${id}`, { association_type: newRole });
      await loadStakeholders();
      toast.success('Role updated');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update role');
    }
  };

  const stakeholdersByRole = useMemo(() => {
    const roleValues = new Set(STAKEHOLDER_ROLES.map(r => r.value));
    const m: Record<string, typeof stakeholders> = {};
    for (const r of STAKEHOLDER_ROLES) m[r.value] = [];
    for (const s of stakeholders) {
      const raw = (s.association_type || 'employees').toLowerCase();
      // Legacy project_team stakeholders show under employees (Option A: no Project Team bucket)
      const role = roleValues.has(raw) ? raw : 'employees';
      m[role].push(s);
    }
    return m;
  }, [stakeholders]);

  const loadComments = useCallback(async (silent = false) => {
    if (!id) return;
    try {
      if (!silent) setCommentsLoading(true);
      const response = await api.get(`/transformations/${id}/comments`);
      if (response.data?.success) {
        // Sort by created_at (latest first)
        const sortedComments = [...(response.data.data || [])].sort((a: any, b: any) => {
          const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
          return bDate - aDate; // Descending order (latest first) for comments/discussions
        });
        setComments(sortedComments);
      } else {
        setComments([]);
      }
    } catch (error: any) {
      if (isActualError(error)) {
        console.error('Error loading comments:', error);
      }
      if (!silent) setComments([]);
    } finally {
      if (!silent) setCommentsLoading(false);
    }
  }, [id]);

  // Live polling: refresh discussion when panel is open so new comments from others appear
  const DISCUSSION_POLL_INTERVAL_MS = 5000;
  useEffect(() => {
    if (activePanel !== 'discussions' || !id) return;
    const intervalId = setInterval(() => loadComments(true), DISCUSSION_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [activePanel, id, loadComments]);

  const loadProjectHistory = async () => {
    if (!id) return;
    try {
      setProjectHistoryLoading(true);
      const response = await api.get(`/transformations/${id}/activity`);
      if (response.data?.success && Array.isArray(response.data.data)) {
        setProjectHistory(response.data.data);
      } else {
        setProjectHistory([]);
      }
    } catch (error: any) {
      if (isActualError(error)) console.error('Error loading project history:', error);
      setProjectHistory([]);
    } finally {
      setProjectHistoryLoading(false);
    }
  };

  const loadActions = async () => {
    try {
      setActionsLoading(true);
      const actionsData = await actionService.getActionsByContext('TRANSFORMATION', id!);
      // Filter out closed/cancelled actions
      const filteredActions = (actionsData || []).filter((action: any) => 
        action.status !== 'CLOSED' && action.status !== 'CANCELLED' && action.status !== 'COMPLETED'
      );
      // Sort by due_date (if available) or created_at (latest first)
      filteredActions.sort((a: any, b: any) => {
        const aDate = a.due_date ? new Date(a.due_date).getTime() : (a.created_at ? new Date(a.created_at).getTime() : 0);
        const bDate = b.due_date ? new Date(b.due_date).getTime() : (b.created_at ? new Date(b.created_at).getTime() : 0);
        return aDate - bDate; // Ascending order (earliest first)
      });
      setActions(filteredActions);
    } catch (error: any) {
      // Only treat as error if it's a communication problem or server error
      if (isActualError(error)) {
        console.error('Error loading actions:', error);
      }
      // Silently set empty array - missing data is not an error
      setActions([]);
    } finally {
      setActionsLoading(false);
    }
  };

  const loadManager = async () => {
    if (!transformation?.manager_id) return;
    try {
      setManagerLoading(true);
      // Try to get user via generic CRUD endpoint
      const response = await api.get(`/generic/users/${transformation.manager_id}`);
      if (response?.data?.success && response.data.data) {
        const userData = response.data.data;
        setManager({
          id: userData.id,
          name: `${userData.first_name || userData.firstName || ''} ${userData.last_name || userData.lastName || ''}`.trim() || userData.email || 'Unknown',
          email: userData.email
        });
      } else if (response?.data?.data) {
        // Handle case where data is directly in response.data
        const userData = response.data.data;
        setManager({
          id: userData.id,
          name: `${userData.first_name || userData.firstName || ''} ${userData.last_name || userData.lastName || ''}`.trim() || userData.email || 'Unknown',
          email: userData.email
        });
      }
    } catch (error: any) {
      // Only treat as error if it's a communication problem or server error
      if (isActualError(error)) {
        console.error('Error loading manager:', error);
      }
      // If manager_id exists but we can't load details, show a placeholder
      if (transformation.manager_id) {
        setManager({
          id: transformation.manager_id,
          name: 'Unknown Manager',
          email: undefined
        });
      } else {
        setManager(null);
      }
    } finally {
      setManagerLoading(false);
    }
  };

  const loadOwner = async () => {
    if (!transformation?.owner_id) return;
    try {
      setOwnerLoading(true);
      // Try to get user via generic CRUD endpoint
      const response = await api.get(`/generic/users/${transformation.owner_id}`);
      if (response?.data?.success && response.data.data) {
        const userData = response.data.data;
        setOwner({
          id: userData.id,
          name: `${userData.first_name || userData.firstName || ''} ${userData.last_name || userData.lastName || ''}`.trim() || userData.email || 'Unknown',
          email: userData.email
        });
      } else if (response?.data?.data) {
        // Handle case where data is directly in response.data
        const userData = response.data.data;
        setOwner({
          id: userData.id,
          name: `${userData.first_name || userData.firstName || ''} ${userData.last_name || userData.lastName || ''}`.trim() || userData.email || 'Unknown',
          email: userData.email
        });
      }
    } catch (error: any) {
      // Only treat as error if it's a communication problem or server error
      if (isActualError(error)) {
        console.error('Error loading owner:', error);
      }
      // If owner_id exists but we can't load details, show a placeholder
      if (transformation.owner_id) {
        setOwner({
          id: transformation.owner_id,
          name: 'Unknown Owner',
          email: undefined
        });
      } else {
        setOwner(null);
      }
    } finally {
      setOwnerLoading(false);
    }
  };

  const searchUsers = async (query: string) => {
    if (!query || query.length < 2) {
      setUserSearchResults([]);
      return;
    }
    try {
      setUserSearchLoading(true);
      const response = await api.get('/generic/users');
      const list: any[] = response.data?.data ?? [];
      const q = query.toLowerCase();
      const filtered = list.filter(
        (u: any) =>
          (!currentUser?.tenantId || u.tenant_id === currentUser.tenantId) &&
          ((u.first_name && u.first_name.toLowerCase().includes(q)) ||
            (u.last_name && u.last_name.toLowerCase().includes(q)) ||
            (u.email && u.email.toLowerCase().includes(q)))
      );
      setUserSearchResults(filtered);
    } catch (error: any) {
      console.error('Error searching users:', error);
      setUserSearchResults([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleUserSelect = async (user: any) => {
    if (!id) {
      toast.error('Project not found');
      return;
    }
    setManagerUpdateInProgress(true);
    try {
      await api.put(`/transformations/${id}`, { manager_id: user.id });
      await loadTransformation();
      setShowManagerModal(false);
      setPickerAnchor(null);
      setUserSearchTerm('');
      setUserSearchResults([]);
      toast.success(t('transformation.managerAssigned') || 'Manager assigned successfully');
    } catch (error: any) {
      console.error('Error assigning manager:', error);
      const data = error?.response?.data;
      const msg = (typeof data?.error === 'string' ? data.error : data?.error?.message) ?? data?.message ?? (t('transformation.failedToAssignManager') || 'Failed to assign manager');
      toast.error(msg);
      setShowManagerModal(false);
      setPickerAnchor(null);
    } finally {
      setManagerUpdateInProgress(false);
    }
  };

  const handleRemoveManager = async () => {
    try {
      // Update transformation to remove manager_id
      await api.put(`/transformations/${id}`, {
        manager_id: null
      });
      
      // Reload transformation to get updated data
      await loadTransformation();
      
      toast.success(t('transformation.managerRemoved') || 'Manager removed successfully');
    } catch (error: any) {
      console.error('Error removing manager:', error);
      toast.error(t('transformation.failedToRemoveManager') || 'Failed to remove manager');
    }
  };

  const searchUsersForOwner = async (query: string) => {
    if (!query || query.length < 2) {
      setOwnerSearchResults([]);
      return;
    }
    try {
      setOwnerSearchLoading(true);
      const response = await api.get('/generic/users');
      const list: any[] = response.data?.data ?? [];
      const q = query.toLowerCase();
      const filtered = list.filter(
        (u: any) =>
          (!currentUser?.tenantId || u.tenant_id === currentUser.tenantId) &&
          ((u.first_name && u.first_name.toLowerCase().includes(q)) ||
            (u.last_name && u.last_name.toLowerCase().includes(q)) ||
            (u.email && u.email.toLowerCase().includes(q)))
      );
      setOwnerSearchResults(filtered);
    } catch {
      setOwnerSearchResults([]);
    } finally {
      setOwnerSearchLoading(false);
    }
  };

  const handleOwnerSelect = async (user: any) => {
    if (!id) {
      toast.error('Project not found');
      setShowOwnerPicker(false);
      setPickerAnchor(null);
      return;
    }
    setOwnerUpdateInProgress(true);
    try {
      await api.put(`/transformations/${id}`, { owner_id: user.id });
      await loadTransformation();
      await loadOwner();
      setShowOwnerPicker(false);
      setPickerAnchor(null);
      setOwnerSearchTerm('');
      setOwnerSearchResults([]);
      toast.success(t('transformation.ownerUpdated') || 'Owner updated');
    } catch (err: any) {
      const data = err?.response?.data;
      const msg = (typeof data?.error === 'string' ? data.error : data?.error?.message) ?? data?.message ?? (t('transformation.failedToUpdateOwner') || 'Failed to update owner');
      toast.error(msg);
      setShowOwnerPicker(false);
      setPickerAnchor(null);
    } finally {
      setOwnerUpdateInProgress(false);
    }
  };

  /**
   * Load all users for action assignment
   */
  const loadActionAssignmentUsers = async () => {
    try {
      setCreateActionUserSearchLoading(true);
      const response = await api.get('/users/admin/users', {
        params: { limit: 1000 } // Get all users in tenant
      });
      
      if (response.data.success) {
        const users = response.data.data?.users || response.data.data || [];
        // Filter to current tenant only (unless platform admin)
        const filteredUsers = users
          .filter((u: any) => !currentUser?.tenantId || u.tenant_id === currentUser.tenantId)
          .map((u: any) => ({
            id: u.id,
            name: `${u.firstName || u.first_name || ''} ${u.lastName || u.last_name || ''}`.trim() || u.email || 'Unknown User',
            email: u.email || ''
          }));
        
        // Ensure current user is always in the list
        if (currentUser?.id) {
          const currentUserInList = filteredUsers.find((u: any) => u.id === currentUser.id);
          if (!currentUserInList) {
            filteredUsers.unshift({
              id: currentUser.id,
              name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || currentUser.email || 'You',
              email: currentUser.email || ''
            });
          }
        }
        
        setCreateActionAllUsers(filteredUsers);
        // Show all unassigned users initially (but always show current user)
        setCreateActionUserSearchResults(filteredUsers.filter((u: any) => 
          !createActionAssignedUserIds.includes(u.id) || u.id === currentUser?.id
        ));
      }
    } catch (error: any) {
      console.error('Error loading users:', error);
      // Even if API fails, ensure current user is available
      if (currentUser?.id) {
        const currentUserObj = {
          id: currentUser.id,
          name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || currentUser.email || 'You',
          email: currentUser.email || ''
        };
        setCreateActionAllUsers([currentUserObj]);
        if (!createActionAssignedUserIds.includes(currentUser.id)) {
          setCreateActionUserSearchResults([currentUserObj]);
        }
      }
    } finally {
      setCreateActionUserSearchLoading(false);
    }
  };

  /**
   * Search users for action assignment (filter from already loaded users)
   */
  const searchActionAssignmentUsers = (query: string) => {
    if (!query || query.length < 1) {
      // If no query, show all unassigned users (but always show current user)
      setCreateActionUserSearchResults(createActionAllUsers.filter((u: any) => 
        !createActionAssignedUserIds.includes(u.id) || u.id === currentUser?.id
      ));
      return;
    }
    
    // Filter loaded users by search query (name or email)
    // Always include current user if they match the search, even if already assigned
    const queryLower = query.toLowerCase();
    const filtered = createActionAllUsers.filter((u: any) => {
      const matchesSearch = u.name.toLowerCase().includes(queryLower) || u.email.toLowerCase().includes(queryLower);
      const isCurrentUser = u.id === currentUser?.id;
      // Show if: (not assigned OR is current user) AND matches search
      return matchesSearch && (!createActionAssignedUserIds.includes(u.id) || isCurrentUser);
    });
    setCreateActionUserSearchResults(filtered);
  };

  /**
   * Handle assigning user to action
   */
  const handleAssignActionUser = (userId: string) => {
    if (!createActionAssignedUserIds.includes(userId)) {
      setCreateActionAssignedUserIds(prev => [...prev, userId]);
      setCreateActionUserSearchTerm('');
      setShowCreateActionUserSearch(false);
    }
  };

  /**
   * Handle unassigning user from action
   */
  const handleUnassignActionUser = (userId: string) => {
    setCreateActionAssignedUserIds(prev => prev.filter(id => id !== userId));
  };

  /**
   * Load users when search is opened
   */
  useEffect(() => {
    if (showCreateActionUserSearch && createActionAllUsers.length === 0) {
      loadActionAssignmentUsers();
    } else if (showCreateActionUserSearch) {
      searchActionAssignmentUsers(createActionUserSearchTerm);
    }
  }, [showCreateActionUserSearch]);

  /**
   * Handle user search input change
   */
  useEffect(() => {
    if (showCreateActionUserSearch && createActionAllUsers.length > 0) {
      const timeoutId = setTimeout(() => {
        searchActionAssignmentUsers(createActionUserSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [createActionUserSearchTerm, showCreateActionUserSearch, createActionAllUsers, createActionAssignedUserIds, currentUser?.id]);

  /**
   * Close user search dropdown when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (createActionUserSearchRef.current && !createActionUserSearchRef.current.contains(event.target as Node)) {
        setShowCreateActionUserSearch(false);
      }
    };

    if (showCreateActionUserSearch) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCreateActionUserSearch]);

  /**
   * Initialize action form with current user as default assignee
   */
  useEffect(() => {
    if (showCreateForm === 'actions' && currentUser?.id && createActionAssignedUserIds.length === 0) {
      setCreateActionAssignedUserIds([currentUser.id]);
    }
  }, [showCreateForm, currentUser?.id]);

  // Create handlers (no titles - project context is the title)
  const handleCreateAction = async () => {
    if (!createActionForm.description.trim()) {
      toast.error(t('transformation.actionDescriptionRequired') || 'Action description is required');
      return;
    }
    try {
      await actionService.createAction({
        title: '', // Title is auto-generated from description by backend
        description: createActionForm.description,
        priority: createActionForm.priority,
        due_date: createActionForm.due_date ? createActionForm.due_date : undefined,
        context_type: 'TRANSFORMATION',
        context_id: id!,
        assigned_user_ids: createActionAssignedUserIds.length > 0 ? createActionAssignedUserIds : (currentUser?.id ? [currentUser.id] : undefined)
      });
      toast.success(t('transformation.actionCreated') || 'Action created successfully');
      setCreateActionForm({ description: '', priority: 'MEDIUM', due_date: '' });
      setCreateActionAssignedUserIds([]);
      setCreateActionUserSearchTerm('');
      setCreateActionUserSearchResults([]);
      setShowCreateForm(null);
      await loadActions();
    } catch (error) {
      console.error('Error creating action:', error);
      toast.error(t('transformation.failedToCreateAction') || 'Failed to create action');
    }
  };

  const handleCreateIssue = async () => {
    if (!createIssueForm.description.trim()) {
      toast.error(t('transformation.issueDescriptionRequired') || 'Issue description is required');
      return;
    }
    try {
      const { issueService } = await import('@/services/issueService');
      await issueService.createIssue({
        title: '', // No title in project context
        description: createIssueForm.description,
        urgency: createIssueForm.urgency,
        impact: createIssueForm.impact,
        target_type: 'TRANSFORMATION',
        target_id: id!
      });
      toast.success(t('transformation.issueCreated') || 'Issue created successfully');
      setCreateIssueForm({ description: '', urgency: 'MEDIUM', impact: 'MEDIUM' });
      setShowCreateForm(null);
      await loadIssues();
    } catch (error) {
      console.error('Error creating issue:', error);
      toast.error(t('transformation.failedToCreateIssue') || 'Failed to create issue');
    }
  };

  const handleCreateRisk = async () => {
    if (!createRiskForm.description.trim()) {
      toast.error(t('transformation.riskDescriptionRequired') || 'Risk description is required');
      return;
    }
    try {
      await api.post('/risks', {
        name: createRiskForm.name.trim() || '', // Empty name will be auto-generated from description
        description: createRiskForm.description,
        severity: createRiskForm.severity,
        probability: createRiskForm.probability,
        transformation_id: id!
      });
      toast.success(t('transformation.riskCreated') || 'Risk created successfully');
      setCreateRiskForm({ name: '', description: '', severity: 'MEDIUM', probability: 'MEDIUM' });
      setShowCreateForm(null);
      await loadRisks();
    } catch (error) {
      console.error('Error creating risk:', error);
      toast.error(t('transformation.failedToCreateRisk') || 'Failed to create risk');
    }
  };

  const handleCreateDiscussion = async () => {
    if (!createDiscussionForm.content.trim()) {
      toast.error(t('transformation.discussionContentRequired') || 'Discussion content is required');
      return;
    }
    try {
      // Find if there's already a main discussion for this transformation
      const mainDiscussion = comments.find((c: any) => c.comment_type === 'discussion');
      
      if (mainDiscussion) {
        // Add a reply to the existing discussion
        await api.post(`/discussions/${mainDiscussion.id}/replies`, {
          content: createDiscussionForm.content
        });
        toast.success(t('transformation.commentAdded') || 'Comment added successfully');
      } else {
        // Create a new discussion (first comment)
        await api.post('/discussions', {
          title: '', // No title in project context - project is the discussion thread
          content: createDiscussionForm.content,
          category: 'TRANSFORMATION',
          mentioned_entities: [
            {
              entity_type: 'project',
              entity_id: id!
            }
          ]
        });
        toast.success(t('transformation.discussionCreated') || 'Discussion created successfully');
      }
      setCreateDiscussionForm({ content: '' });
      await loadComments();
    } catch (error) {
      console.error('Error creating discussion/comment:', error);
      toast.error(t('transformation.failedToCreateDiscussion') || 'Failed to create discussion');
    }
  };

  const handleCloseRisk = async (riskId: string) => {
    try {
      await api.put(`/transformations/${id}/risks/${riskId}/close`);
      toast.success(t('transformation.riskClosed') || 'Risk closed');
      loadRisks();
    } catch (error) {
      console.error('Error closing risk:', error);
      toast.error(t('transformation.failedToCloseRisk') || 'Failed to close risk');
    }
  };

  const handleCloseIssue = async (issueId: string) => {
    try {
      await api.put(`/transformations/${id}/issues/${issueId}/close`);
      toast.success(t('transformation.issueClosed') || 'Issue closed');
      loadIssues();
    } catch (error) {
      console.error('Error closing issue:', error);
      toast.error(t('transformation.failedToCloseIssue') || 'Failed to close issue');
    }
  };

  const diffProjectStatus = (
    next: typeof projectStatus,
    previous: typeof projectStatus
  ): Partial<typeof projectStatus> => {
    const patch: Partial<typeof projectStatus> = {};
    (Object.keys(next) as Array<keyof typeof projectStatus>).forEach((key) => {
      if (next[key] !== previous[key]) {
        (patch as Record<string, unknown>)[key] = next[key];
      }
    });
    return patch;
  };

  // Autosave only the fields that changed (chunked patch)
  const autosaveProjectStatusPatch = useCallback(async (patch: Partial<typeof projectStatus>) => {
    if (!id || isReadOnly) return;
    const keys = Object.keys(patch);
    if (keys.length === 0) return;
    try {
      lastSavedStatusRef.current = { ...lastSavedStatusRef.current, ...patch };
      await api.post(`/transformations/${id}/project-status`, patch);
    } catch (error: any) {
      if (isActualError(error)) {
        console.error('Error autosaving project status:', error);
        toast.error(t('transformation.failedToSaveProjectStatus') || 'Failed to save project status');
      }
    }
  }, [id, isReadOnly, t]);

  // Track if this is the initial load to prevent saving empty state
  const isInitialLoad = useRef(true);

  // Debounced autosave - triggers 2 seconds after last change
  useEffect(() => {
    // Don't autosave on initial load (when transformation is not loaded yet)
    if (!transformation || !id) {
      isInitialLoad.current = true;
      return;
    }

    // Skip first save after transformation loads (initial state)
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      void autosaveProjectStatusPatch(diffProjectStatus(projectStatus, lastSavedStatusRef.current));
    }, 2000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        void autosaveProjectStatusPatch(diffProjectStatus(projectStatus, lastSavedStatusRef.current));
      }
    };
  }, [projectStatus, autosaveProjectStatusPatch, transformation, id]);

  // After Operations writes or Undo, refresh this page. Do not reset the chat.
  useEffect(() => {
    lastMutationEpoch.current = mutationEpoch;
  }, [id]);

  useEffect(() => {
    if (!id || mutationEpoch === lastMutationEpoch.current) return;
    lastMutationEpoch.current = mutationEpoch;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    isInitialLoad.current = true;
    void (async () => {
      try {
        const data = await transformationsService.getTransformation(id);
        setTransformation(data);
        const statusResponse = await api.get(`/transformations/${id}/project-status`);
        if (statusResponse.data?.success && statusResponse.data.data) {
          const latestStatus = Array.isArray(statusResponse.data.data)
            ? statusResponse.data.data[0]
            : statusResponse.data.data;
          applyProjectStatusRow(latestStatus);
        }
      } catch (statusError: any) {
        if (isActualError(statusError)) {
          console.error('Error refreshing project after Operations write:', statusError);
        }
      }
      void loadInchstones();
      void loadInchstoneStats();
      void loadRisks();
      void loadIssues();
      void loadActions();
      void loadProjectHistory();
    })();
  }, [mutationEpoch, id]);


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatHeaderMoney = (value: unknown): string | null => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    if (!Number.isFinite(n) || n === 0) return null;
    const abs = Math.abs(n);
    const sign = n < 0 ? '-' : '';
    if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(1)}B`;
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
    return `${sign}$${Math.round(abs).toLocaleString('en-US')}`;
  };


  // Debounced autosave functionality - removed as it's not currently used
  // const autosaveTransformation = useCallback(async (field: string, value: any) => {
  //   try {
  //     if (field === 'organizational_hierarchy_id') {
  //             }
  //     // For hierarchy fields, convert empty strings to null
  //     let saveValue = value;
  //     if (field.includes('hierarchy')) {
  //       if (!value || value === '') {
  //         saveValue = null;
  //       }
  //       // Remove validation logic that was causing race condition
  //     }
  //     
  //     const response = await api.put(`/transformations/${id}`, {
  //       [field]: saveValue
  //     });
  //     
  //     if (response.data.success) {
  //             }
  //   } catch (error: any) {
  //     console.error('Error saving transformation:', error);
  //     if (error.response?.data?.message?.includes('foreign key constraint')) {
  //               return;
  //     }
  //   }
  // }, [id]);

  // Inchstone management functions
  const handleCreateInchstone = async (parentId?: string) => {
    try {
      const newInchstone = {
        name: `${t('transformation.newInchstone')} ${Date.now()}`,
        description: '',
        start_date: new Date().toISOString().split('T')[0],
        due_date: null,
        task_type: 'General',
        status: 'PENDING',
        priority: 'MEDIUM',
        parent_id: parentId || null
      };

      const response = await api.post(`/inchstones/transformations/${id}/inchstones`, newInchstone);
      
      if (response.data.success) {
        // Reload inchstones and stats
        await loadInchstones();
        await loadInchstoneStats();
      } else {
        console.error('Failed to create inchstone:', response.data.message);
      }
    } catch (error) {
      console.error('Error creating inchstone:', error);
    }
  };

  const handleReorderInchstones = async (inchstoneId: string, newSortOrder: number, newParentId?: string) => {
    try {
      
      const response = await api.put(`/inchstones/${inchstoneId}/reorder`, {
        sort_order: newSortOrder,
        parent_id: newParentId
      });
      
      if (response.data.success) {
        await loadInchstones();
        toast.success(t('transformation.inchstoneReorderedSuccessfully'));
      } else {
        console.error('Failed to reorder inchstone:', response.data.message);
        toast.error(t('transformation.failedToReorderInchstone'));
      }
    } catch (error) {
      console.error('Error reordering inchstone:', error);
      toast.error(t('transformation.failedToReorderInchstone'));
    }
  };

  const [editingInchstone, setEditingInchstone] = useState<Inchstone | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    due_date: '',
    task_type: 'General',
    priority: 'MEDIUM',
    status: 'PENDING'
  });

  const handleEditInchstone = (inchstone: Inchstone) => {
    setEditingInchstone(inchstone);
    setEditFormData({
      name: inchstone.name,
      description: inchstone.description || '',
      due_date: inchstone.due_date ? inchstone.due_date.split('T')[0] : '',
      task_type: inchstone.task_type,
      priority: inchstone.priority,
      status: inchstone.status
    });
  };

  const handleSaveEdit = async () => {
    if (!editingInchstone) return;

    try {
      const response = await api.put(`/inchstones/${editingInchstone.id}`, editFormData);
      
      if (response.data.success) {
        // Reload inchstones and stats
        loadInchstones();
        loadInchstoneStats();
        toast.success(t('transformation.inchstoneUpdatedSuccessfully'));
        setEditingInchstone(null);
      } else {
        console.error('Failed to update inchstone:', response.data.message);
        toast.error(t('transformation.failedToUpdateInchstone'));
      }
    } catch (error) {
      console.error('Error updating inchstone:', error);
      toast.error(t('transformation.failedToUpdateInchstone'));
    }
  };

  const handleCancelEdit = () => {
    setEditingInchstone(null);
    setEditFormData({
      name: '',
      description: '',
      due_date: '',
      task_type: 'General',
      priority: 'MEDIUM',
      status: 'PENDING'
    });
  };

  const handleDeleteInchstone = async (inchstoneId: string) => {
    if (!confirm(t('transformation.confirmDeleteInchstone'))) {
      return;
    }

    try {
      await api.delete(`/inchstones/${inchstoneId}?cascade=true`);
      await loadInchstones();
      await loadInchstoneStats();
      toast.success(t('transformation.inchstoneDeletedSuccessfully'));
    } catch (err) {
      console.error('Error deleting inchstone:', err);
      toast.error(t('transformation.failedToDeleteInchstone'));
    }
  };

  const handleToggleInchstoneStatus = async (inchstoneId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'COMPLETED' ? 'IN_PROGRESS' : 'COMPLETED';
      const endpoint = newStatus === 'COMPLETED' ? 'complete' : 'incomplete';
      
      await api.put(`/inchstones/${inchstoneId}/${endpoint}`);
      
      await loadInchstones();
      await loadInchstoneStats();
      toast.success(newStatus === 'COMPLETED' ? t('transformation.inchstoneMarkedAsCompleted') : t('transformation.inchstoneMarkedAsInProgress'));
    } catch (err) {
      console.error('Error updating inchstone status:', err);
      toast.error(t('transformation.failedToUpdateInchstoneStatus'));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">{t('transformation.loadingTransformation')}</div>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="text-red-600 mb-4 font-semibold">Access Denied</div>
          <div className="text-gray-500 mb-4">{accessDeniedReason}</div>
          <Link to="/transformations">
            <Button variant="primary">{t('transformation.backToTransformations')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!transformation) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="text-gray-500 mb-4">{t('transformation.transformationNotFound')}</div>
          <Link to="/transformations">
            <Button variant="primary">{t('transformation.backToTransformations')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const headerCost = formatHeaderMoney(transformation.budget ?? transformation.calculated_cost);
  const headerBenefitOutcomes = Array.isArray(transformation.expected_outcomes)
    ? transformation.expected_outcomes.map((item) => String(item).trim()).filter(Boolean)
    : [];
  const headerBenefitMoney = kpis.reduce((sum, kpi) => {
    const amount = Number(kpi.target_value);
    if (!Number.isFinite(amount) || amount === 0) return sum;
    const unit = String(kpi.unit || '').toLowerCase();
    const name = String(kpi.name || '').toLowerCase();
    const isMoney =
      unit.includes('usd') ||
      unit.includes('currency') ||
      unit.includes('$') ||
      unit.includes('dollar');
    const isBenefit = /benefit|value|saving|productivity|run.?rate/.test(name);
    return isMoney || isBenefit ? sum + amount : sum;
  }, 0);
  const headerBenefits =
    formatHeaderMoney(headerBenefitMoney) ||
    (headerBenefitOutcomes.length ? headerBenefitOutcomes.join(', ') : null);
  const rawObjectives = transformation.objectives as unknown;
  const headerObjectives = Array.isArray(rawObjectives)
    ? rawObjectives.map((item) => String(item).trim()).filter(Boolean)
    : typeof rawObjectives === 'string' && rawObjectives.trim()
      ? [rawObjectives.trim()]
      : [];

  return (
    <>
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="bg-white rounded-lg shadow-sm border border-[var(--color-border)] p-4">
          {/* Header */}
          <div className="mb-4 pb-4">
            {/* Line 1: Project Title | Settings | Discussions | View/Edit */}
            <div className="flex items-center gap-6 mb-3">
              <h1 className="text-xl font-semibold text-[var(--color-surface-text)] flex-1">{transformation.name}</h1>
              
              <div className="flex items-center gap-4 text-sm">
                {/* Project Settings - opens side panel (full edit still available inside panel) */}
                {!isReadOnly && (
                  <button
                    onClick={() => setActivePanel('settings')}
                    className="flex items-center justify-center p-2 rounded-lg hover:bg-[var(--color-hover-bg)] transition-colors"
                    title={t('transformation.projectSettings') || 'Project settings'}
                    type="button"
                    aria-label={t('transformation.projectSettings') || 'Project settings'}
                  >
                    <Cog6ToothIcon className="w-5 h-5 text-[var(--color-secondary-text)]" />
                  </button>
                )}
                {/* Discussion Icon with Comment Count */}
                <button
                  onClick={() => setActivePanel('discussions')}
                  className="relative flex items-center justify-center p-2 rounded-lg hover:bg-[var(--color-hover-bg)] transition-colors"
                  title={t('transformation.viewDiscussions') || 'View Discussions'}
                >
                  <ChatBubbleLeftRightIcon className="w-5 h-5 text-[var(--color-secondary-text)]" />
                  {comments.length > 0 && (
                    <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-semibold text-white bg-[var(--color-info)] rounded-full">
                      {comments.length}
                    </span>
                  )}
                </button>
                <ProjectModeToggle
                  mode={isReadOnly ? 'view' : 'edit'}
                  canEdit={!permissionBasedReadOnly && isProjectMember}
                  onChange={(mode) => {
                    navigate(`/transformations/${id}`, {
                      replace: true,
                      state: { viewMode: mode === 'view' }
                    });
                  }}
                />
                {/* Project History */}
                <button
                  onClick={() => { setActivePanel('history'); loadProjectHistory(); }}
                  className="flex items-center justify-center p-2 rounded-lg hover:bg-[var(--color-hover-bg)] transition-colors"
                  title={t('transformation.projectHistory') || 'Project history'}
                >
                  <ClockIcon className="w-5 h-5 text-[var(--color-secondary-text)]" />
                </button>
              </div>
            </div>

            {/* Line 2: Objectives | Hierarchies (right) */}
            <div className="flex items-center gap-6 mb-3">
              <div className="flex-1 min-w-0 text-sm">
                <span className="text-[var(--color-secondary-text)]">
                  {t('transformation.objectives') || 'Objectives'}:
                </span>{' '}
                {headerObjectives.length > 0 ? (
                  <span className="text-[var(--color-surface-text)]">{headerObjectives.join(', ')}</span>
                ) : (
                  <span className="text-[var(--color-secondary-text)] italic">
                    {translateWithFallback('transformation.noObjectives', 'No objectives provided')}
                  </span>
                )}
              </div>

              {/* Hierarchies - Right Side (names resolve only for ACTIVE nodes in tenant) */}
              <div className="flex items-center gap-6 text-sm">
                {transformation.organizational_hierarchy_id && (
                  <div className="flex items-center gap-2">
                    <span className="text-[var(--color-secondary-text)]">Org:</span>
                    <span
                      className={
                        transformation.organizational_hierarchy_name
                          ? 'text-[var(--color-surface-text)]'
                          : 'italic text-[var(--color-surface-text-secondary)]'
                      }
                    >
                      {transformation.organizational_hierarchy_name
                        || t('transformation.hierarchyLegacyInactive')
                        || 'Legacy hierarchy (inactive)'}
                    </span>
                  </div>
                )}
                {transformation.functional_hierarchy_id && (
                  <div className="flex items-center gap-2">
                    <span className="text-[var(--color-secondary-text)]">Func:</span>
                    <span
                      className={
                        transformation.functional_hierarchy_name
                          ? 'text-[var(--color-surface-text)]'
                          : 'italic text-[var(--color-surface-text-secondary)]'
                      }
                    >
                      {transformation.functional_hierarchy_name
                        || t('transformation.hierarchyLegacyInactive')
                        || 'Legacy hierarchy (inactive)'}
                    </span>
                  </div>
                )}
                {transformation.geographic_hierarchy_id && (
                  <div className="flex items-center gap-2">
                    <span className="text-[var(--color-secondary-text)]">Geo:</span>
                    <span
                      className={
                        transformation.geographic_hierarchy_name
                          ? 'text-[var(--color-surface-text)]'
                          : 'italic text-[var(--color-surface-text-secondary)]'
                      }
                    >
                      {transformation.geographic_hierarchy_name
                        || t('transformation.hierarchyLegacyInactive')
                        || 'Legacy hierarchy (inactive)'}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Line 3: Start / End / Cost / Benefits | Owner | Manager | Stakeholders */}
            <div className="flex items-center gap-6 mb-3">
              <div className="flex flex-wrap items-center gap-4 text-sm min-w-0 flex-1">
                {transformation.start_date && (
                  <div className="flex items-center gap-2">
                    <span className="text-[var(--color-secondary-text)]">Start:</span>
                    <span className="text-[var(--color-surface-text)]">{formatDate(transformation.start_date)}</span>
                  </div>
                )}
                {transformation.end_date && (
                  <div className="flex items-center gap-2">
                    <span className="text-[var(--color-secondary-text)]">End:</span>
                    <span className="text-[var(--color-surface-text)]">{formatDate(transformation.end_date)}</span>
                  </div>
                )}
                {headerCost && (
                  <div className="flex items-center gap-2">
                    <span className="text-[var(--color-secondary-text)]">
                      {translateWithFallback('transformation.headerCost', 'Cost')}:
                    </span>
                    <span className="text-[var(--color-surface-text)]">{headerCost}</span>
                  </div>
                )}
                {headerBenefits && (
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[var(--color-secondary-text)] shrink-0">
                      {translateWithFallback('transformation.headerBenefits', 'Benefits')}:
                    </span>
                    <span
                      className="text-[var(--color-surface-text)] max-w-[14rem] truncate"
                      title={headerBenefits}
                    >
                      {headerBenefits}
                    </span>
                  </div>
                )}
              </div>

              {/* Owner, Manager, Stakeholders - Right Aligned */}
              <div className="flex items-center gap-6 ml-auto">
                {/* Owner */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[var(--color-secondary-text)]">Owner:</span>
                  {ownerLoading ? (
                    <span className="text-sm text-[var(--color-secondary-text)]">Loading...</span>
                  ) : owner ? (
                    <span className="text-sm font-medium text-[var(--color-surface-text)]">{owner.name}</span>
                  ) : transformation.owner_name ? (
                    <span className="text-sm font-medium text-[var(--color-surface-text)]">{transformation.owner_name}</span>
                  ) : (
                    <span className="text-sm text-[var(--color-secondary-text)] italic">Unknown</span>
                  )}
                </div>

                {/* Manager */}
                <div className="flex items-center gap-2 relative" ref={userSearchRef}>
                  <span className="text-sm text-[var(--color-secondary-text)]">Manager:</span>
                  {transformation.manager_id ? (
                    <>
                      {managerLoading ? (
                        <span className="text-sm text-[var(--color-secondary-text)]">Loading...</span>
                      ) : manager ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[var(--color-surface-text)]">{manager.name}</span>
                          {!isReadOnly && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleRemoveManager}
                              className="h-5 px-1 text-xs"
                              title={t('transformation.removeManager') || 'Remove Manager'}
                            >
                              ×
                            </Button>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-[var(--color-secondary-text)]">Unknown</span>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-[var(--color-secondary-text)] italic">Unassigned</span>
                      {!isReadOnly && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setPickerAnchor({ top: r.top, left: r.left, width: r.width, height: r.height });
                            setShowManagerModal(true);
                          }}
                          className="h-5 px-2 text-xs"
                        >
                          {t('transformation.assignManager') || 'Assign'}
                        </Button>
                      )}
                    </div>
                  )}
                  
                  {/* User Search Dropdown - rendered in header when panel is closed; portaled when panel open so it appears above panel */}
                </div>

                {/* Stakeholders - always clickable to open panel */}
                <button
                  type="button"
                  onClick={() => { setActivePanel('stakeholders'); loadMembers(); }}
                  className="flex items-center gap-2 text-left rounded px-1 -mx-1 hover:bg-[var(--color-hover-bg)] transition-colors"
                  title={t('transformation.viewAll') || 'View stakeholders'}
                >
                  <span className="text-sm text-[var(--color-secondary-text)]">{t('transformation.stakeholders.headerLabel') || 'Stakeholders:'}</span>
                  <span className="text-sm font-semibold text-[var(--color-surface-text)] underline decoration-dotted cursor-pointer">
                    {SHOW_POSITION_STAKEHOLDERS ? stakeholders.length + positionStakeholders.reduce((sum, ps) => sum + (ps.user_count || 0), 0) : stakeholders.length}
                  </span>
                </button>
              </div>
            </div>

          </div>

          {/* 2x2 Grid Layout */}
          <div className="overflow-y-auto">
            <div className="grid grid-cols-2 gap-6">
          {/* Upper Left: Project Status */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 h-[calc(50vh-3rem)] flex flex-col min-h-0">
            <div className="grid grid-cols-2 grid-rows-2 gap-3 flex-1 min-h-0 h-full">
              {/* Overall Summary - Top Left */}
              <div className="flex h-full min-h-0 flex-col">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-700">{t('transformation.overallSummary') || 'Overall Summary'}</label>
                  <button
                    onClick={async () => {
                      if (isReadOnly) return;
                      const currentStatus = projectStatus.overall_status;
                      const nextStatus: 'RED' | 'YELLOW' | 'GREEN' = currentStatus === 'RED' ? 'YELLOW' : currentStatus === 'YELLOW' ? 'GREEN' : 'RED';
                      const updatedStatus = {...projectStatus, overall_status: nextStatus};
                      setProjectStatus(updatedStatus);
                      await autosaveProjectStatusPatch({ overall_status: nextStatus });
                    }}
                    disabled={isReadOnly}
                    className={`w-6 h-6 rounded-full transition-all ${
                      projectStatus.overall_status === 'RED' 
                        ? 'bg-red-800 ring-2 ring-red-500' 
                        : projectStatus.overall_status === 'YELLOW'
                        ? 'bg-yellow-500 ring-2 ring-yellow-300'
                        : 'bg-green-800 ring-2 ring-green-500'
                    } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-110'}`}
                    title={
                      projectStatus.overall_status === 'RED' 
                        ? (t('transformation.statusSeriouslyBehind') || 'Seriously behind - Click to change')
                        : projectStatus.overall_status === 'YELLOW'
                        ? (t('transformation.statusMinorIssues') || 'Minor issues - Click to change')
                        : (t('transformation.statusOnTrack') || 'On track - Click to change')
                    }
                  />
                </div>
                <div className="min-h-0 flex-1">
                <textarea
                  value={projectStatus.overall_summary}
                  onChange={(e) => setProjectStatus({...projectStatus, overall_summary: e.target.value})}
                  disabled={isReadOnly}
                  readOnly={isReadOnly}
                  className={`h-full w-full resize-none px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs ${isReadOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                  placeholder={isReadOnly ? '' : (t('transformation.enterOverallSummary') || 'Enter overall project summary...')}
                />
                </div>
              </div>

              {/* Cost Summary - Top Right */}
              <div className="flex h-full min-h-0 flex-col">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-700">{t('transformation.costSummary') || 'Cost Summary'}</label>
                  <button
                    onClick={async () => {
                      if (isReadOnly) return;
                      const currentStatus = projectStatus.cost_status;
                      const nextStatus: 'RED' | 'YELLOW' | 'GREEN' = currentStatus === 'RED' ? 'YELLOW' : currentStatus === 'YELLOW' ? 'GREEN' : 'RED';
                      const updatedStatus = {...projectStatus, cost_status: nextStatus};
                      setProjectStatus(updatedStatus);
                      await autosaveProjectStatusPatch({ cost_status: nextStatus });
                    }}
                    disabled={isReadOnly}
                    className={`w-6 h-6 rounded-full transition-all ${
                      projectStatus.cost_status === 'RED' 
                        ? 'bg-red-800 ring-2 ring-red-500' 
                        : projectStatus.cost_status === 'YELLOW'
                        ? 'bg-yellow-500 ring-2 ring-yellow-300'
                        : 'bg-green-800 ring-2 ring-green-500'
                    } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-110'}`}
                    title={
                      projectStatus.cost_status === 'RED' 
                        ? (t('transformation.statusSeriouslyBehind') || 'Seriously behind - Click to change')
                        : projectStatus.cost_status === 'YELLOW'
                        ? (t('transformation.statusMinorIssues') || 'Minor issues - Click to change')
                        : (t('transformation.statusOnTrack') || 'On track - Click to change')
                    }
                  />
                </div>
                <div className="min-h-0 flex-1">
                <textarea
                  value={projectStatus.cost_summary}
                  onChange={(e) => setProjectStatus({...projectStatus, cost_summary: e.target.value})}
                  disabled={isReadOnly}
                  readOnly={isReadOnly}
                  className={`h-full w-full resize-none px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs ${isReadOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                  placeholder={isReadOnly ? '' : (t('transformation.enterCostSummary') || 'Enter cost summary...')}
                />
                </div>
              </div>

              {/* Benefits Summary - Bottom Left */}
              <div className="flex h-full min-h-0 flex-col">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-700">{t('transformation.benefitsSummary') || 'Benefits Summary'}</label>
                  <button
                    onClick={async () => {
                      if (isReadOnly) return;
                      const currentStatus = projectStatus.benefits_status;
                      const nextStatus: 'RED' | 'YELLOW' | 'GREEN' = currentStatus === 'RED' ? 'YELLOW' : currentStatus === 'YELLOW' ? 'GREEN' : 'RED';
                      const updatedStatus = {...projectStatus, benefits_status: nextStatus};
                      setProjectStatus(updatedStatus);
                      await autosaveProjectStatusPatch({ benefits_status: nextStatus });
                    }}
                    disabled={isReadOnly}
                    className={`w-6 h-6 rounded-full transition-all ${
                      projectStatus.benefits_status === 'RED' 
                        ? 'bg-red-800 ring-2 ring-red-500' 
                        : projectStatus.benefits_status === 'YELLOW'
                        ? 'bg-yellow-500 ring-2 ring-yellow-300'
                        : 'bg-green-800 ring-2 ring-green-500'
                    } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-110'}`}
                    title={
                      projectStatus.benefits_status === 'RED' 
                        ? (t('transformation.statusSeriouslyBehind') || 'Seriously behind - Click to change')
                        : projectStatus.benefits_status === 'YELLOW'
                        ? (t('transformation.statusMinorIssues') || 'Minor issues - Click to change')
                        : (t('transformation.statusOnTrack') || 'On track - Click to change')
                    }
                  />
                </div>
                <div className="min-h-0 flex-1">
                <textarea
                  value={projectStatus.benefits_summary}
                  onChange={(e) => setProjectStatus({...projectStatus, benefits_summary: e.target.value})}
                  disabled={isReadOnly}
                  readOnly={isReadOnly}
                  className={`h-full w-full resize-none px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs ${isReadOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                  placeholder={isReadOnly ? '' : (t('transformation.enterBenefitsSummary') || 'Enter benefits summary...')}
                />
                </div>
              </div>

              {/* Execution Health Summary - Bottom Right */}
              <div className="flex h-full min-h-0 flex-col">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-700">{t('transformation.executionHealthSummary') || 'Execution Health Summary'}</label>
                  <button
                    onClick={async () => {
                      if (isReadOnly) return;
                      const currentStatus = projectStatus.execution_health_status;
                      const nextStatus: 'RED' | 'YELLOW' | 'GREEN' = currentStatus === 'RED' ? 'YELLOW' : currentStatus === 'YELLOW' ? 'GREEN' : 'RED';
                      const updatedStatus = {...projectStatus, execution_health_status: nextStatus};
                      setProjectStatus(updatedStatus);
                      await autosaveProjectStatusPatch({ execution_health_status: nextStatus });
                    }}
                    disabled={isReadOnly}
                    className={`w-6 h-6 rounded-full transition-all ${
                      projectStatus.execution_health_status === 'RED' 
                        ? 'bg-red-800 ring-2 ring-red-500' 
                        : projectStatus.execution_health_status === 'YELLOW'
                        ? 'bg-yellow-500 ring-2 ring-yellow-300'
                        : 'bg-green-800 ring-2 ring-green-500'
                    } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-110'}`}
                    title={
                      projectStatus.execution_health_status === 'RED' 
                        ? (t('transformation.statusSeriouslyBehind') || 'Seriously behind - Click to change')
                        : projectStatus.execution_health_status === 'YELLOW'
                        ? (t('transformation.statusMinorIssues') || 'Minor issues - Click to change')
                        : (t('transformation.statusOnTrack') || 'On track - Click to change')
                    }
                  />
                </div>
                <div className="min-h-0 flex-1">
                <textarea
                  value={projectStatus.execution_health_summary}
                  onChange={(e) => setProjectStatus({...projectStatus, execution_health_summary: e.target.value})}
                  disabled={isReadOnly}
                  readOnly={isReadOnly}
                  className={`h-full w-full resize-none px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs ${isReadOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                  placeholder={isReadOnly ? '' : (t('transformation.enterExecutionHealthSummary') || 'Enter execution health summary...')}
                />
                </div>
              </div>
            </div>
          </div>

          {/* Upper Right: Inchstones */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 overflow-y-auto" style={{ maxHeight: 'calc(50vh - 3rem)' }}>
          {/* Inchstone Summary */}
          <div className="mb-4">
              <div className="grid grid-cols-4 gap-2 mb-3">
                <div className="text-center p-2 bg-blue-50 rounded">
                  <div className="text-lg font-bold text-blue-600">{isNaN(inchstoneStats.total_inchstones) ? 0 : inchstoneStats.total_inchstones}</div>
                  <div className="text-xs text-gray-600">{t('transformation.total')}</div>
                </div>
                <div className="text-center p-2 bg-green-50 rounded">
                  <div className="text-lg font-bold text-green-600">{isNaN(inchstoneStats.completed_inchstones) ? 0 : inchstoneStats.completed_inchstones}</div>
                  <div className="text-xs text-gray-600">{t('transformation.done')}</div>
                </div>
                <div className="text-center p-2 bg-yellow-50 rounded">
                  <div className="text-lg font-bold text-yellow-600">{(isNaN(inchstoneStats.total_inchstones) ? 0 : inchstoneStats.total_inchstones) - (isNaN(inchstoneStats.completed_inchstones) ? 0 : inchstoneStats.completed_inchstones)}</div>
                  <div className="text-xs text-gray-600">{t('common.active')}</div>
                </div>
                <div className="text-center p-2 bg-red-50 rounded">
                  <div className="text-lg font-bold text-red-600">{isNaN(inchstoneStats.overdue_inchstones) ? 0 : inchstoneStats.overdue_inchstones}</div>
                  <div className="text-xs text-gray-600">{t('transformation.overdue')}</div>
                </div>
                </div>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-600">{t('transformation.progress')}:</span>
                <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                  <div 
                    className="bg-blue-600 h-1.5 rounded-full transition-all duration-300" 
                    style={{ width: `${isNaN(inchstoneStats.completion_percentage) ? 0 : inchstoneStats.completion_percentage}%` }}
                        ></div>
                      </div>
                <span className="text-xs font-medium text-gray-900">{isNaN(inchstoneStats.completion_percentage) ? 0 : inchstoneStats.completion_percentage}%</span>
              </div>
            </div>

            {/* Inchstone Management */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">{t('transformation.inchstones')}</h3>
                {!isReadOnly && (
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={handleCreateInchstonesWithAI} disabled={creatingWithAI}>
                      {creatingWithAI ? (t('transformation.creatingWithAI') || 'Creating...') : (t('transformation.createWithAI') || 'Create with AI')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={openTemplateModal}>
                      {t('transformation.applyProjectStructureTemplate') || 'Apply Template'}
                    </Button>
                    <Button size="sm" onClick={() => handleCreateInchstone()}>
                      <PlusIcon className="w-4 h-4 mr-1" />
                      {t('transformation.addInchstone')}
                    </Button>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {inchstoneError && (
                <div className="bg-red-50 border border-red-200 rounded p-2 mb-3">
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className="w-4 h-4 text-red-500 mr-1" />
                    <span className="text-xs text-red-700">{inchstoneError}</span>
                      </div>
                    </div>
                  )}

              {/* Inchstone List */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {inchstoneLoading ? (
                  <div className="text-center py-8">
                    <div className="text-gray-500">{t('transformation.loadingInchstones') || 'Loading inchstones...'}</div>
                  </div>
                ) : inchstones.length === 0 ? (
                  <div className="text-center py-8">
                    <ClockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <div className="text-gray-500 mb-4">{t('transformation.noInchstonesYet') || 'No inchstones yet'}</div>
                    {!isReadOnly && (
                      <Button onClick={() => handleCreateInchstone()}>
                        <PlusIcon className="w-4 h-4 mr-2" />
                        {t('transformation.createFirstInchstone') || 'Create First Inchstone'}
                      </Button>
                    )}
                  </div>
                ) : (
                  <InchstoneHierarchy
                    inchstones={inchstones}
                    onEdit={isReadOnly ? () => {} : handleEditInchstone}
                    onDelete={isReadOnly ? () => {} : handleDeleteInchstone}
                    onToggleStatus={isReadOnly ? () => {} : handleToggleInchstoneStatus}
                    onCreateChild={isReadOnly ? () => {} : handleCreateInchstone}
                    onReorder={isReadOnly ? () => {} : handleReorderInchstones}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Lower Left: Issues, Risks, Actions - 3 Columns */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 overflow-y-auto" style={{ maxHeight: 'calc(50vh - 3rem)' }}>
            <div className="grid grid-cols-3 gap-4">
              {/* Issues Column */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t('transformation.issues') || 'Issues'} ({issues.length})
                  </h3>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" onClick={() => setActivePanel('issues')} className="h-6 px-2 text-xs" title={t('transformation.viewAll') || 'View All'}>
                      <EyeIcon className="w-3 h-3" />
                    </Button>
                    {!isReadOnly && (
                      <Button size="sm" onClick={() => setShowCreateForm('issues')} className="h-6 px-2 text-xs" title={t('transformation.createIssue') || 'Create Issue'}>
                        <PlusIcon className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
                {issuesLoading ? (
                  <div className="text-center py-4 text-gray-500 text-xs">{t('transformation.loadingIssues') || 'Loading issues...'}</div>
                ) : issues.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 text-xs">None</div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {issues.slice(0, 10).map((issue) => (
                      <div key={issue.id} className="border border-gray-200 rounded p-2 text-xs">
                        <div className="flex items-start justify-between mb-1">
                          <span className="text-gray-900 text-xs line-clamp-2">{issue.description || issue.title}</span>
                          {issue.status !== 'CLOSED' && !isReadOnly && (
                            <Button size="sm" variant="outline" onClick={() => handleCloseIssue(issue.id)} className="h-5 px-1.5 text-xs">
                              {t('transformation.close') || 'Close'}
                            </Button>
                          )}
                        </div>
                        <span className={`px-1.5 py-0.5 text-xs rounded ${
                          issue.status === 'CLOSED' ? 'bg-gray-100 text-gray-800' :
                          issue.status === 'RESOLVED' ? 'bg-green-100 text-green-800' :
                          issue.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {issue.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Risks Column */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t('transformation.risks') || 'Risks'} ({risks.length})
                  </h3>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" onClick={() => setActivePanel('risks')} className="h-6 px-2 text-xs" title={t('transformation.viewAll') || 'View All'}>
                      <EyeIcon className="w-3 h-3" />
                    </Button>
                    {!isReadOnly && (
                      <Button size="sm" onClick={() => setShowCreateForm('risks')} className="h-6 px-2 text-xs" title={t('transformation.createRisk') || 'Create Risk'}>
                        <PlusIcon className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
                {risksLoading ? (
                  <div className="text-center py-4 text-gray-500 text-xs">{t('transformation.loadingRisks') || 'Loading risks...'}</div>
                ) : risks.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 text-xs">None</div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {risks.slice(0, 10).map((risk) => (
                      <div key={risk.id} className="border border-gray-200 rounded p-2 text-xs">
                        <div className="flex items-start justify-between mb-1">
                          <span className="text-gray-900 text-xs line-clamp-2">{risk.description || risk.name}</span>
                          {(risk.risk_status || risk.status) !== 'CLOSED' &&
                            (risk.risk_status || risk.status) !== 'RESOLVED' &&
                            !isReadOnly && (
                            <Button size="sm" variant="outline" onClick={() => handleCloseRisk(risk.id)} className="h-5 px-1.5 text-xs">
                              {t('transformation.close') || 'Close'}
                            </Button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          <span className={`px-1.5 py-0.5 text-xs rounded ${
                            risk.severity === 'CRITICAL' ? 'bg-red-100 text-red-800' :
                            risk.severity === 'HIGH' ? 'bg-orange-100 text-orange-800' :
                            risk.severity === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {risk.severity}
                          </span>
                          <span className={`px-1.5 py-0.5 text-xs rounded ${
                            (risk.risk_status || risk.status) === 'CLOSED' ||
                            (risk.risk_status || risk.status) === 'RESOLVED'
                              ? 'bg-gray-100 text-gray-800'
                              : (risk.risk_status || risk.status) === 'MITIGATED'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-blue-100 text-blue-800'
                          }`}>
                            {risk.risk_status || risk.status}
                          </span>
                        </div>
                        {risk.comments && risk.comments.length > 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            {risk.comments.length} comment{risk.comments.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions Column */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t('transformation.actions') || 'Actions'} ({actions.length})
                  </h3>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" onClick={() => setActivePanel('actions')} className="h-6 px-2 text-xs" title={t('transformation.viewAll') || 'View All'}>
                      <EyeIcon className="w-3 h-3" />
                    </Button>
                    {!isReadOnly && (
                      <Button size="sm" onClick={() => setShowCreateForm('actions')} className="h-6 px-2 text-xs" title={t('transformation.createAction') || 'Create Action'}>
                        <PlusIcon className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
                {actionsLoading ? (
                  <div className="text-center py-4 text-gray-500 text-xs">{t('transformation.loadingActions') || 'Loading actions...'}</div>
                ) : actions.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 text-xs">None</div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {actions.slice(0, 10).map((action) => (
                      <div key={action.id} className="border border-gray-200 rounded p-2 text-xs">
                        <div className="flex items-start justify-between mb-1">
                          <span className="text-gray-900 text-xs line-clamp-2">{action.description || action.name}</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          <span className={`px-1.5 py-0.5 text-xs rounded ${
                            action.priority === 'CRITICAL' ? 'bg-red-100 text-red-800' :
                            action.priority === 'HIGH' ? 'bg-orange-100 text-orange-800' :
                            action.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {action.priority}
                          </span>
                          <span className={`px-1.5 py-0.5 text-xs rounded ${
                            action.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                            action.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                            action.status === 'PENDING' ? 'bg-gray-100 text-gray-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {action.status}
                          </span>
                        </div>
                        {action.due_date && (
                          <div className="text-xs text-gray-500 mt-1">
                            Due: {new Date(action.due_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Lower Right: Next Steps and Asks */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 overflow-y-auto" style={{ maxHeight: 'calc(50vh - 3rem)' }}>
            <div className="grid grid-cols-2 gap-4">
              {/* Next Steps - Left Block */}
              <textarea
                value={projectStatus.next_steps}
                onChange={(e) => setProjectStatus({...projectStatus, next_steps: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                rows={12}
                placeholder={t('transformation.nextSteps') || 'Next Steps'}
              />

              {/* Asks - Right Block */}
              <textarea
                value={projectStatus.asks}
                onChange={(e) => setProjectStatus({...projectStatus, asks: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                rows={12}
                placeholder={t('transformation.asks') || 'Asks'}
              />
            </div>
          </div>
        </div>
      </div>
      </div>
      </div>
      </div>

      {/* Edit Inchstone Modal */}
      {editingInchstone && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('transformation.editInchstone')}</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.name')}</label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
        </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('transformation.dueDate')}</label>
                <input
                  type="date"
                  value={editFormData.due_date}
                  onChange={(e) => setEditFormData({...editFormData, due_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('transformation.taskType')}</label>
                <select
                  value={editFormData.task_type}
                  onChange={(e) => setEditFormData({...editFormData, task_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="General">{t('transformation.taskTypeGeneral')}</option>
                  <option value="Risk_Management">{t('transformation.taskTypeRiskManagement')}</option>
                  <option value="Change_Management">{t('transformation.taskTypeChangeManagement')}</option>
                  <option value="Communication">{t('transformation.taskTypeCommunication')}</option>
                  <option value="People">{t('transformation.taskTypePeople')}</option>
                  <option value="Process">{t('transformation.taskTypeProcess')}</option>
                  <option value="Technology">{t('transformation.taskTypeTechnology')}</option>
                  <option value="Customer">{t('transformation.taskTypeCustomer')}</option>
                  <option value="Supplier">{t('transformation.taskTypeSupplier')}</option>
                </select>
            </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('transformation.priority')}</label>
                <select
                  value={editFormData.priority}
                  onChange={(e) => setEditFormData({...editFormData, priority: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="LOW">{t('transformation.priorityLow')}</option>
                  <option value="MEDIUM">{t('transformation.priorityMedium')}</option>
                  <option value="HIGH">{t('transformation.priorityHigh')}</option>
                  <option value="CRITICAL">{t('transformation.priorityCritical')}</option>
                </select>
                </div>

                <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.status')}</label>
                <select
                  value={editFormData.status}
                  onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="PENDING">{t('transformation.pending')}</option>
                  <option value="IN_PROGRESS">{t('transformation.inProgress')}</option>
                  <option value="COMPLETED">{t('transformation.completed')}</option>
                  <option value="ON_HOLD">{t('transformation.onHold')}</option>
                  <option value="CANCELLED">{t('transformation.cancelled')}</option>
                </select>
              </div>
                  </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] bg-[var(--color-secondary-bg)] text-[var(--color-surface-text)] hover:bg-[var(--color-hover-bg)]"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] bg-[var(--color-info)] text-white hover:opacity-90"
              >
                {t('transformation.saveChanges')}
              </button>
                </div>
              </div>
            </div>
      )}

      {templateModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {t('transformation.selectProjectStructureTemplate') || 'Select project structure template'}
                </h3>
                <p className="text-sm text-gray-500">
                  {t('transformation.selectProjectStructureTemplateDescription') ||
                    'Choose a saved inchstone hierarchy to attach to this project.'}
                </p>
              </div>
              <button
                onClick={closeTemplateModal}
                className="text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] text-2xl leading-none"
                aria-label={t('common.close') || 'Close'}
              >
                ×
              </button>
            </div>
            <div className="px-6 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
              {templateError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded">
                  {templateError}
                </div>
              )}
              {templateLoading ? (
                <div className="text-center py-8 text-gray-500">
                  {t('transformation.loadingTemplates') || 'Loading templates...'}
                </div>
              ) : projectStructureTemplates.length === 0 ? (
                <div className="text-center py-8 space-y-3">
                  <p className="text-gray-500">
                    {t('transformation.noProjectStructureTemplates') || 'No project structure templates yet.'}
                  </p>
                  <Button variant="outline" onClick={() => navigate('/templates/manage')}>
                    {t('transformation.createProjectStructureTemplate') || 'Go to Template Manager'}
                  </Button>
                </div>
              ) : (
                projectStructureTemplates.map(template => (
                  <label
                    key={template.id}
                    className={`flex items-start space-x-3 border rounded-xl p-4 cursor-pointer ${
                      selectedTemplateId === template.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                    }`}
                  >
                    <input
                      type="radio"
                      name="project-structure-template"
                      className="mt-1"
                      checked={selectedTemplateId === template.id}
                      onChange={() => setSelectedTemplateId(template.id)}
                    />
                    <div>
                      <div className="flex items-center justify-between">
                        <h4 className="text-base font-semibold text-gray-900">{template.name}</h4>
                        <span className="text-xs text-gray-500">
                          {t('transformation.updated')}:{' '}
                          {template.updated_at ? new Date(template.updated_at).toLocaleDateString() : '--'}
                        </span>
                      </div>
                      {template.description && (
                        <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {t('transformation.templateVisibility') || 'Visibility'}:{' '}
                        {template.marketplace_visibility}
                      </p>
                    </div>
                  </label>
                ))
              )}
            </div>
            <div className="border-t px-6 py-4 flex justify-between items-center">
              <p className="text-xs text-gray-500">
                {t('transformation.projectStructureTemplateHint') ||
                  'Templates are scoped to your tenant and user account.'}
              </p>
              <div className="flex items-center gap-3">
                <Button variant="ghost" onClick={closeTemplateModal}>
                  {t('common.cancel') || 'Cancel'}
                </Button>
                <Button
                  onClick={handleApplyProjectStructureTemplate}
                  loading={applyingTemplate}
                  disabled={projectStructureTemplates.length === 0}
                >
                  {t('transformation.applyTemplate') || 'Apply template'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contextual Panels */}
      {/* Related Risks Panel */}
      <ContextualPanel
        title="Related Risks"
        items={risks.map((risk): ContextualPanelItem => ({
          id: risk.id,
          title: risk.description || risk.name,
          description: `${risk.severity} - ${risk.risk_status || risk.status}`,
          status: `${risk.severity} - ${risk.risk_status || risk.status}`,
          metadata: risk,
          onClick: () => {
            navigate(`/risk/${risk.id}`);
            setActivePanel(null);
          },
          actions: (risk.risk_status || risk.status) !== 'CLOSED' &&
            (risk.risk_status || risk.status) !== 'RESOLVED' ? [
            {
              label: 'Close',
              onClick: () => {
                handleCloseRisk(risk.id);
                setActivePanel(null);
              },
              variant: 'secondary'
            }
          ] : undefined
        }))}
        isLoading={risksLoading}
        emptyMessage="None"
        onCreateNew={isReadOnly ? undefined : () => setShowCreateForm('risks')}
        onCreateLabel="Create Risk"
        showCreateForm={showCreateForm === 'risks'}
        onCancelCreate={() => {
          setShowCreateForm(null);
          setCreateRiskForm({ name: '', description: '', severity: 'MEDIUM', probability: 'MEDIUM' });
        }}
        renderCreateForm={() => (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                {t('common.description') || 'Description'} *
              </label>
              <textarea
                value={createRiskForm.description}
                onChange={(e) => setCreateRiskForm({ ...createRiskForm, description: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] text-sm"
                rows={3}
                placeholder={t('transformation.enterRiskDescription') || 'Enter risk description...'}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                  {t('transformation.severity') || 'Severity'}
                </label>
                <select
                  value={createRiskForm.severity}
                  onChange={(e) => setCreateRiskForm({ ...createRiskForm, severity: e.target.value as any })}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] text-sm"
                >
                  <option value="LOW">{t('transformation.low') || 'Low'}</option>
                  <option value="MEDIUM">{t('transformation.medium') || 'Medium'}</option>
                  <option value="HIGH">{t('transformation.high') || 'High'}</option>
                  <option value="CRITICAL">{t('transformation.critical') || 'Critical'}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                  {t('transformation.probability') || 'Probability'}
                </label>
                <select
                  value={createRiskForm.probability}
                  onChange={(e) => setCreateRiskForm({ ...createRiskForm, probability: e.target.value as any })}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] text-sm"
                >
                  <option value="LOW">{t('transformation.low') || 'Low'}</option>
                  <option value="MEDIUM">{t('transformation.medium') || 'Medium'}</option>
                  <option value="HIGH">{t('transformation.high') || 'High'}</option>
                  <option value="CRITICAL">{t('transformation.critical') || 'Critical'}</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreateRisk} className="flex-1">
                {t('transformation.create') || 'Create'}
              </Button>
              <Button variant="ghost" onClick={() => {
                setShowCreateForm(null);
                setCreateRiskForm({ name: '', description: '', severity: 'MEDIUM', probability: 'MEDIUM' });
              }} className="flex-1">
                {t('common.cancel') || 'Cancel'}
              </Button>
            </div>
          </div>
        )}
        isOpen={activePanel === 'risks'}
        onClose={() => {
          setActivePanel(null);
          setShowCreateForm(null);
        }}
      />

      {/* Related KPIs Panel */}
      <ContextualPanel
        title="Related KPIs"
        items={kpis.map((kpi): ContextualPanelItem => ({
          id: kpi.tenant_kpi_id || kpi.id,
          title: kpi.name,
          description: kpi.description,
          status: kpi.is_primary_kpi ? 'Primary KPI' : undefined,
          metadata: {
            ...kpi,
            target_value: kpi.target_value,
            unit: kpi.unit,
            relationship_type: kpi.relationship_type
          },
          onClick: () => {
            navigate(`/kpis/${kpi.tenant_kpi_id || kpi.id}`);
            setActivePanel(null);
          }
        }))}
        isLoading={kpisLoading}
        emptyMessage="None"
        onCreateNew={() => {
          navigate(`/kpis/create?transformation_id=${id}`);
          setActivePanel(null);
        }}
        onCreateLabel="Create KPI"
        isOpen={activePanel === 'kpis'}
        onClose={() => setActivePanel(null)}
      />

      {/* Related Issues Panel */}
      <ContextualPanel
        title="Related Issues"
        items={issues.map((issue): ContextualPanelItem => ({
          id: issue.id,
          title: issue.description || issue.title,
          description: issue.status,
          status: issue.status,
          metadata: issue,
          onClick: () => {
            navigate(`/issues/${issue.id}`);
            setActivePanel(null);
          },
          actions: issue.status !== 'CLOSED' ? [
            {
              label: 'Close',
              onClick: () => {
                handleCloseIssue(issue.id);
                setActivePanel(null);
              },
              variant: 'secondary'
            }
          ] : undefined
        }))}
        isLoading={issuesLoading}
        emptyMessage="None"
        onCreateNew={isReadOnly ? undefined : () => setShowCreateForm('issues')}
        onCreateLabel="Create Issue"
        showCreateForm={showCreateForm === 'issues'}
        onCancelCreate={() => {
          setShowCreateForm(null);
          setCreateIssueForm({ description: '', urgency: 'MEDIUM', impact: 'MEDIUM' });
        }}
        renderCreateForm={() => (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                {t('common.description') || 'Description'} *
              </label>
              <textarea
                value={createIssueForm.description}
                onChange={(e) => setCreateIssueForm({ ...createIssueForm, description: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] text-sm"
                rows={3}
                placeholder={t('transformation.enterIssueDescription') || 'Enter issue description...'}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                  {t('transformation.urgency') || 'Urgency'}
                </label>
                <select
                  value={createIssueForm.urgency}
                  onChange={(e) => setCreateIssueForm({ ...createIssueForm, urgency: e.target.value as any })}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] text-sm"
                >
                  <option value="LOW">{t('transformation.low') || 'Low'}</option>
                  <option value="MEDIUM">{t('transformation.medium') || 'Medium'}</option>
                  <option value="HIGH">{t('transformation.high') || 'High'}</option>
                  <option value="CRITICAL">{t('transformation.critical') || 'Critical'}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                  {t('transformation.impact') || 'Impact'}
                </label>
                <select
                  value={createIssueForm.impact}
                  onChange={(e) => setCreateIssueForm({ ...createIssueForm, impact: e.target.value as any })}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] text-sm"
                >
                  <option value="LOW">{t('transformation.low') || 'Low'}</option>
                  <option value="MEDIUM">{t('transformation.medium') || 'Medium'}</option>
                  <option value="HIGH">{t('transformation.high') || 'High'}</option>
                  <option value="CRITICAL">{t('transformation.critical') || 'Critical'}</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreateIssue} className="flex-1">
                {t('transformation.create') || 'Create'}
              </Button>
              <Button variant="ghost" onClick={() => {
                setShowCreateForm(null);
                setCreateIssueForm({ description: '', urgency: 'MEDIUM', impact: 'MEDIUM' });
              }} className="flex-1">
                {t('common.cancel') || 'Cancel'}
              </Button>
            </div>
          </div>
        )}
        isOpen={activePanel === 'issues'}
        onClose={() => {
          setActivePanel(null);
          setShowCreateForm(null);
        }}
      />

      {/* Unified Team & Stakeholders Panel: Owner, PM, Members (under the hood different APIs) + role buckets */}
      <ContextualPanel
        title={t('transformation.stakeholders.panelTitle') || 'Team & Stakeholders'}
        hideHeader
        disableItemHover
        items={[]}
        isLoading={stakeholdersLoading}
        emptyMessage=""
        isOpen={activePanel === 'stakeholders'}
        onClose={() => { setActivePanel(null); setShowOwnerPicker(false); setShowManagerModal(false); setPickerAnchor(null); }}
        customContentWrapperClassName="pt-2"
        customContent={
          <div className="space-y-4 px-2 pb-4">
            {/* Project leadership: editable Owner & Manager */}
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-3 space-y-3">
              <h4 className="text-sm font-semibold text-[var(--color-surface-text)]">{t('transformation.projectLeadership') || 'Project leadership'}</h4>
              <div className="space-y-2 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[var(--color-surface-text)] font-medium shrink-0">{t('transformation.owner') || 'Owner'}:</span>
                  {ownerLoading ? <span className="text-[var(--color-secondary-text)]">…</span> : owner ? <span className="font-semibold text-[var(--color-surface-text)]">{owner.name}</span> : <span className="text-[var(--color-secondary-text)]">{transformation?.owner_name ?? '—'}</span>}
                  {!isReadOnly && id && (
                    <button type="button" onClick={(e) => { const r = (e.currentTarget as HTMLElement).getBoundingClientRect(); setPickerAnchor({ top: r.top, left: r.left, width: r.width, height: r.height }); setShowOwnerPicker(true); setOwnerSearchTerm(''); setOwnerSearchResults([]); }} className="text-xs px-2 py-0.5 rounded border border-[var(--color-border)] hover:bg-[var(--color-hover-bg)]">{t('transformation.change') || 'Change'}</button>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[var(--color-surface-text)] font-medium shrink-0">{t('transformation.projectManager') || 'Project manager'}:</span>
                  {managerLoading ? <span className="text-[var(--color-secondary-text)]">…</span> : manager ? <span className="font-semibold text-[var(--color-surface-text)]">{manager.name}</span> : transformation?.manager_id ? <span className="text-[var(--color-secondary-text)]">—</span> : <span className="italic text-[var(--color-secondary-text)]">{t('transformation.unassigned') || 'Unassigned'}</span>}
                  {!isReadOnly && id && (
                    <span className="flex items-center gap-1">
                      <button type="button" onClick={(e) => { const r = (e.currentTarget as HTMLElement).getBoundingClientRect(); setPickerAnchor({ top: r.top, left: r.left, width: r.width, height: r.height }); setShowManagerModal(true); }} className="text-xs px-2 py-0.5 rounded border border-[var(--color-border)] hover:bg-[var(--color-hover-bg)]">{t('transformation.change') || 'Change'}</button>
                      {transformation?.manager_id && <button type="button" onClick={handleRemoveManager} className="text-xs px-2 py-0.5 rounded border border-[var(--color-danger)] text-[var(--color-danger)] hover:bg-[var(--color-danger)]/10">{t('transformation.remove') || 'Remove'}</button>}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Members (under the hood: transformation_members API) */}
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-3 space-y-2">
              <h4 className="text-sm font-semibold text-[var(--color-surface-text)]">{t('transformation.members') || 'Members'}</h4>
              {membersLoading ? (
                <p className="text-sm text-[var(--color-secondary-text)]">{t('common.loading')}</p>
              ) : (
                <ul className="space-y-1.5">
                  {members.length === 0 ? (
                    <li className="text-sm text-[var(--color-secondary-text)]">{t('transformation.noMembersYet') || 'No members yet. Add below.'}</li>
                  ) : (
                    members.map((m) => {
                      const name = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || m.user_id;
                      return (
                        <li key={m.id} className="flex items-center justify-between gap-2 py-1.5">
                          <span className="text-sm font-medium text-[var(--color-surface-text)]">{name}{m.email ? <span className="text-[var(--color-secondary-text)]"> ({m.email})</span> : ''}{m.role ? <span className="text-[var(--color-secondary-text)]"> · {m.role}</span> : ''}</span>
                          {!isReadOnly && id && (
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  await transformationsService.removeMember(id, m.user_id);
                                  await loadMembers();
                                  toast.success(t('transformation.memberRemoved') || 'Member removed');
                                } catch (err: any) {
                                  toast.error(err?.response?.data?.error ?? (t('transformation.failedToRemoveMember') || 'Failed to remove member'));
                                }
                              }}
                              className="p-1 rounded text-[var(--color-danger)] hover:bg-[var(--color-danger)]/10"
                              title={t('transformation.removeMember') || 'Remove member'}
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          )}
                        </li>
                      );
                    })
                  )}
                </ul>
              )}
              {!isReadOnly && id && (
                <div ref={addMemberSearchRef} className="relative pt-2 border-t border-[var(--color-border)]">
                  <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('transformation.addMemberLabel') || 'Add member — type 2+ characters, then click a name'}</label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={addMemberSearch}
                        onChange={(e) => { setAddMemberSearch(e.target.value); setShowAddMemberDropdown(true); }}
                        onFocus={() => { if (addMemberSearchResults.length > 0) setShowAddMemberDropdown(true); }}
                        placeholder={t('transformation.searchByNameOrEmail') || 'Search by name or email...'}
                        className="w-full px-2 py-1.5 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                        disabled={addMemberSubmitting}
                      />
                      {showAddMemberDropdown && (addMemberSearchResults.length > 0 || addMemberSearchLoading) && (
                        <div className="absolute z-[100] w-full mt-1 rounded-md shadow-lg max-h-60 overflow-auto bg-[var(--color-surface-bg)] border border-[var(--color-border)]">
                          {addMemberSearchLoading && <div className="px-3 py-2.5 text-sm text-[var(--color-surface-text)]">{t('transformation.searching') || 'Searching…'}</div>}
                          {!addMemberSearchLoading && addMemberSearch.trim().length < 2 && <div className="px-3 py-2.5 text-sm text-[var(--color-surface-text)]">{t('transformation.typeAtLeast2Chars') || 'Type at least 2 characters'}</div>}
                          {!addMemberSearchLoading && addMemberSearch.trim().length >= 2 && addMemberSearchResults.length === 0 && <div className="px-3 py-2.5 text-sm text-[var(--color-surface-text)]">{t('transformation.noUsersFound') || 'No users found'}</div>}
                          {addMemberSearchResults.map((u) => (
                            <button
                              key={u.id}
                              type="button"
                              onClick={async () => {
                                if (!id || addMemberSubmitting) return;
                                setAddMemberSubmitting(true);
                                try {
                                  await transformationsService.addMember(id, u.id, addMemberRole || undefined);
                                  await loadMembers();
                                  setAddMemberSearch('');
                                  setAddMemberSearchResults([]);
                                  setShowAddMemberDropdown(false);
                                  toast.success(t('transformation.memberAdded') || 'Member added');
                                } catch (err: any) {
                                  toast.error(err?.response?.data?.error ?? (t('transformation.failedToAddMember') || 'Failed to add member'));
                                } finally {
                                  setAddMemberSubmitting(false);
                                }
                              }}
                              className="w-full text-left px-3 py-2.5 text-sm bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] hover:bg-[var(--color-hover-bg)] border-b border-[var(--color-border)] last:border-b-0"
                            >
                              <div className="font-semibold">{u.name}</div>
                              {u.email && <div className="text-xs mt-0.5 text-[var(--color-secondary-text)]">{u.email}</div>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <input
                      type="text"
                      value={addMemberRole}
                      onChange={(e) => setAddMemberRole(e.target.value)}
                      placeholder={t('transformation.roleOptional') || 'Role (optional)'}
                      className="w-24 px-2 py-1.5 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Add stakeholder — visible box before role buckets */}
            {!isReadOnly && id && (
              <div className="rounded-lg border-2 border-dashed border-[var(--color-border)] bg-[var(--color-surface-bg)] p-3 space-y-2" ref={stakeholderSearchRef}>
                <h4 className="text-sm font-semibold text-[var(--color-surface-text)]">{t('transformation.addStakeholder') || 'Add stakeholder'}</h4>
                <p className="text-xs text-[var(--color-secondary-text)]">{t('transformation.addStakeholderDescription') || 'Choose a role, then type name or email and click a result to add.'}</p>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-[var(--color-secondary-text)]">Role:</span>
                  <select
                    value={addStakeholderRole}
                    onChange={(e) => setAddStakeholderRole(e.target.value)}
                    className="px-2 py-1.5 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]"
                  >
                    {STAKEHOLDER_ROLES.map((r) => (
                      <option key={r.value} value={r.value}>{t(`stakeholderManagement.roles.${r.value}`) || r.label}</option>
                    ))}
                  </select>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    value={addStakeholderInput}
                    onChange={(e) => {
                      setAddStakeholderInput(e.target.value);
                      setShowStakeholderDropdown(true);
                    }}
                    onFocus={() => { if (stakeholderSearchResults.length > 0) setShowStakeholderDropdown(true); }}
                    placeholder={t('transformation.typeToSearchStakeholder') || 'Type 2+ characters to search (name or email)...'}
                    className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                    disabled={addStakeholderSubmitting}
                  />
                  {showStakeholderDropdown && (stakeholderSearchResults.length > 0 || stakeholderSearchLoading) && (
                    <div className="absolute z-[100] w-full mt-1 rounded-md shadow-lg max-h-60 overflow-auto bg-[var(--color-surface-bg)] border border-[var(--color-border)]">
                      {stakeholderSearchLoading && <div className="px-3 py-2.5 text-sm text-[var(--color-surface-text)]">{t('transformation.searching') || 'Searching...'}</div>}
                      {!stakeholderSearchLoading && addStakeholderInput.trim().length < 2 && <div className="px-3 py-2.5 text-sm text-[var(--color-surface-text)]">{t('transformation.typeAtLeast2Chars') || 'Type at least 2 characters'}</div>}
                      {!stakeholderSearchLoading && addStakeholderInput.trim().length >= 2 && stakeholderSearchResults.length === 0 && <div className="px-3 py-2.5 text-sm text-[var(--color-surface-text)]">{t('transformation.noResults') || 'No results'}</div>}
                      {stakeholderSearchResults.map((result, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={async () => {
                            await handleAddStakeholder(result);
                          }}
                          className="w-full text-left px-3 py-2.5 text-sm bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] hover:bg-[var(--color-hover-bg)] border-b border-[var(--color-border)] last:border-b-0"
                        >
                          <div className="font-semibold">{result.name}</div>
                          {result.email && <div className="text-xs mt-0.5 text-[var(--color-secondary-text)]">{result.email}</div>}
                          {result.type === 'stakeholder' && <div className="text-xs mt-0.5 text-[var(--color-info)]">{t('transformation.existingStakeholder') || 'Existing stakeholder'}</div>}
                          {result.type === 'user' && <div className="text-xs mt-0.5 text-[var(--color-info)]">{t('transformation.user') || 'User'}</div>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Stakeholders by role (Sponsors, Customers, etc.) */}
            <h4 className="text-sm font-semibold text-[var(--color-surface-text)]">{t('transformation.stakeholdersByRole') || 'Stakeholders by role'}</h4>
            <div className="grid grid-cols-2 gap-3">
              {STAKEHOLDER_ROLES.map((roleDef) => {
                const bucketRole = roleDef.value;
                const list = stakeholdersByRole[bucketRole] || [];
                const isDropTarget = dragOverBucket === bucketRole;
                return (
                  <div
                    key={bucketRole}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.dataTransfer.dropEffect = 'move';
                      setDragOverBucket(bucketRole);
                    }}
                    onDragLeave={(e) => {
                      if (e.relatedTarget != null && e.currentTarget.contains(e.relatedTarget as Node)) return;
                      setDragOverBucket(null);
                    }}
                    onDrop={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragOverBucket(null);
                      setDraggingStakeholderId(null);
                      const raw = e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('application/json');
                      if (!raw) return;
                      try {
                        const { stakeholderId, currentRole } = JSON.parse(raw);
                        if (currentRole === bucketRole) return;
                        await handleChangeStakeholderRole(stakeholderId, bucketRole);
                      } catch {
                        // ignore
                      }
                    }}
                    className={`rounded-lg border-2 min-h-[44px] transition-colors ${
                      isDropTarget
                        ? 'border-[var(--color-info)] bg-[var(--color-info)] bg-opacity-10'
                        : 'border-[var(--color-border)] bg-[var(--color-surface-bg)]'
                    }`}
                  >
                    <div className="px-3 py-2 border-b border-[var(--color-border)] flex items-center justify-between bg-[var(--color-secondary-bg)] rounded-t-md">
                      <span className="text-sm font-semibold text-[var(--color-surface-text)]">{t(`stakeholderManagement.roles.${roleDef.value}`) || roleDef.label}</span>
                      <span className="text-xs font-medium text-[var(--color-surface-text)]">{list.length}</span>
                    </div>
                    <div className="p-2 flex flex-wrap gap-2 min-h-[32px]">
                      {list.map((stakeholder) => {
                        const hasDetails = !!(stakeholder.title || stakeholder.email || stakeholder.organization || stakeholder.primary_position_name || stakeholder.business_function_name || stakeholder.geographic_name);
                        const isDragging = draggingStakeholderId === stakeholder.id;
                        return (
                          <div
                            key={stakeholder.id}
                            className="group relative"
                          >
                            <div
                              draggable={!isReadOnly}
                              onDragStart={(e) => {
                                setDraggingStakeholderId(stakeholder.id);
                                const payload = JSON.stringify({
                                  stakeholderId: stakeholder.id,
                                  currentRole: (stakeholder.association_type || 'employees').toLowerCase()
                                });
                                e.dataTransfer.setData('text/plain', payload);
                                e.dataTransfer.setData('application/json', payload);
                                e.dataTransfer.effectAllowed = 'move';
                              }}
                              onDragEnd={() => setDraggingStakeholderId(null)}
                              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                              onDrop={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDragOverBucket(null);
                                setDraggingStakeholderId(null);
                                const raw = e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('application/json');
                                if (!raw) return;
                                try {
                                  const { stakeholderId, currentRole } = JSON.parse(raw);
                                  if (currentRole === bucketRole) return;
                                  await handleChangeStakeholderRole(stakeholderId, bucketRole);
                                } catch {
                                  // ignore
                                }
                              }}
                              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] text-sm select-none min-w-0 ${
                                isDragging ? 'opacity-50' : ''
                              } ${!isReadOnly ? 'cursor-grab active:cursor-grabbing' : ''}`}
                            >
                              <span className="font-semibold text-[var(--color-surface-text)] truncate min-w-0 flex-1">{stakeholder.name}</span>
                              {!isReadOnly && (
                                <button
                                  type="button"
                                  draggable={false}
                                  onPointerDown={(ev) => ev.stopPropagation()}
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    handleRemoveStakeholder(stakeholder.id);
                                  }}
                                  className="ml-auto p-1 rounded text-[var(--color-danger)] hover:bg-[var(--color-danger)]/10 shrink-0"
                                  title={t('transformation.stakeholders.remove') || 'Remove'}
                                >
                                  <XMarkIcon className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                            {hasDetails && (
                              <div
                                className="pointer-events-none absolute left-0 top-full z-50 mt-1 hidden w-56 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-2 shadow-lg group-hover:block"
                                role="tooltip"
                              >
                                <div className="text-xs font-medium text-[var(--color-surface-text)] mb-1">{stakeholder.name}</div>
                                <dl className="space-y-0.5 text-xs text-[var(--color-secondary-text)]">
                                  {stakeholder.title && <div><dt className="inline font-medium">{t('transformation.stakeholders.detailTitle') || 'Title: '}</dt><dd className="inline">{stakeholder.title}</dd></div>}
                                  {stakeholder.email && <div><dt className="inline font-medium">{t('transformation.stakeholders.detailEmail') || 'Email: '}</dt><dd className="inline break-all">{stakeholder.email}</dd></div>}
                                  {stakeholder.organization && <div><dt className="inline font-medium">{t('transformation.stakeholders.detailOrg') || 'Org: '}</dt><dd className="inline">{stakeholder.organization}</dd></div>}
                                  {stakeholder.primary_position_name && <div><dt className="inline font-medium">{t('transformation.stakeholders.detailPosition') || 'Position: '}</dt><dd className="inline">{stakeholder.primary_position_name}</dd></div>}
                                  {stakeholder.business_function_name && <div><dt className="inline font-medium">{t('transformation.stakeholders.detailFunction') || 'Function: '}</dt><dd className="inline">{stakeholder.business_function_name}</dd></div>}
                                  {stakeholder.geographic_name && <div><dt className="inline font-medium">{t('transformation.stakeholders.detailGeo') || 'Geo: '}</dt><dd className="inline">{stakeholder.geographic_name}</dd></div>}
                                </dl>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            {SHOW_POSITION_STAKEHOLDERS && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-[var(--color-surface-text)]">{t('transformation.stakeholders.positionStakeholders') || 'Position Stakeholders'}</h4>
                  {!isReadOnly && id && (
                    <PositionStakeholderSelector
                      entityType="TRANSFORMATION"
                      entityId={id}
                      existingPositionIds={positionStakeholders.map(ps => ps.position_id)}
                      onPositionAssigned={() => {
                        loadPositionStakeholders();
                        loadStakeholders();
                      }}
                    />
                  )}
                </div>
                {id && (
                  <PositionStakeholderList
                    entityType="TRANSFORMATION"
                    entityId={id}
                    onPositionRemoved={() => {
                      loadPositionStakeholders();
                      loadStakeholders();
                    }}
                    onPositionUpdated={() => {
                      loadPositionStakeholders();
                      loadStakeholders();
                    }}
                    canEdit={!isReadOnly}
                  />
                )}
              </div>
            )}
          </div>
        }
      />

      {/* Portals: manager and owner pickers above panel (z-[100]); left-aligned to side pane so they stay on screen */}
      {typeof document !== 'undefined' && showManagerModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-label="Change project manager">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setShowManagerModal(false); setPickerAnchor(null); }} />
          <div
            className="absolute z-[101] w-full max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] shadow-xl p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={pickerAnchor ? (() => { const w = typeof window !== 'undefined' ? window.innerWidth : 400; const h = typeof window !== 'undefined' ? window.innerHeight : 600; const paneWidth = 448; const left = Math.max(16, w - paneWidth); const cardH = 400; const below = pickerAnchor.top + pickerAnchor.height + 8; const openAbove = below + cardH > h; const top = openAbove ? Math.max(8, pickerAnchor.top - cardH - 8) : below; return { top, left, maxHeight: cardH }; })() : { top: '6rem', left: '50%', transform: 'translateX(-50%)' }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[var(--color-surface-text)]">Change project manager</h3>
              <button type="button" onClick={() => { setShowManagerModal(false); setPickerAnchor(null); }} disabled={managerUpdateInProgress} className="p-1 rounded hover:bg-[var(--color-hover-bg)] text-[var(--color-secondary-text)] disabled:opacity-50"><XMarkIcon className="w-5 h-5" /></button>
            </div>
            {managerUpdateInProgress && <div className="mb-3 py-2 text-sm text-[var(--color-info)]">Updating…</div>}
            <input
              type="text"
              value={userSearchTerm}
              onChange={(e) => { setUserSearchTerm(e.target.value); searchUsers(e.target.value); }}
              placeholder={t('transformation.searchUsers') || 'Search users...'}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] text-sm mb-2 disabled:opacity-60"
              autoFocus
              disabled={managerUpdateInProgress}
            />
            {userSearchLoading && <div className="py-4 text-center text-sm text-[var(--color-secondary-text)]">Loading…</div>}
            {!userSearchLoading && userSearchTerm.length < 2 && <div className="py-4 text-center text-sm text-[var(--color-secondary-text)]">Type at least 2 characters to search</div>}
            {!userSearchLoading && userSearchTerm.length >= 2 && userSearchResults.length === 0 && <div className="py-4 text-center text-sm text-[var(--color-secondary-text)]">No users found</div>}
            {!userSearchLoading && userSearchResults.length > 0 && (
              <div className="relative z-[102] mt-2 rounded-md shadow-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)]">
                <ul className="max-h-48 overflow-y-auto divide-y divide-[var(--color-border)]">
                  {userSearchResults.map((user: any) => (
                    <li key={user.id}>
                      <button
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (!managerUpdateInProgress) handleUserSelect(user); }}
                        disabled={managerUpdateInProgress}
                        className="w-full px-4 py-2.5 text-left text-sm disabled:opacity-60 disabled:pointer-events-none bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] hover:bg-[var(--color-hover-bg)] border-0 cursor-pointer"
                      >
                        <span className="font-semibold">{`${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() || user.email || 'Unknown'}</span>
                        {user.email && <span className="block text-xs mt-0.5 text-[var(--color-secondary-text)]">{user.email}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
      {typeof document !== 'undefined' && showOwnerPicker && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-label="Change project owner">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setShowOwnerPicker(false); setOwnerSearchTerm(''); setOwnerSearchResults([]); setPickerAnchor(null); }} />
          <div
            className="absolute z-[101] w-full max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] shadow-xl p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={pickerAnchor ? (() => { const w = typeof window !== 'undefined' ? window.innerWidth : 400; const h = typeof window !== 'undefined' ? window.innerHeight : 600; const paneWidth = 448; const left = Math.max(16, w - paneWidth); const cardH = 400; const below = pickerAnchor.top + pickerAnchor.height + 8; const openAbove = below + cardH > h; const top = openAbove ? Math.max(8, pickerAnchor.top - cardH - 8) : below; return { top, left, maxHeight: cardH }; })() : { top: '6rem', left: '50%', transform: 'translateX(-50%)' }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[var(--color-surface-text)]">Change project owner</h3>
              <button type="button" onClick={() => { setShowOwnerPicker(false); setOwnerSearchTerm(''); setOwnerSearchResults([]); setPickerAnchor(null); }} disabled={ownerUpdateInProgress} className="p-1 rounded hover:bg-[var(--color-hover-bg)] text-[var(--color-secondary-text)] disabled:opacity-50"><XMarkIcon className="w-5 h-5" /></button>
            </div>
            {ownerUpdateInProgress && <p className="mb-3 text-sm text-[var(--color-info)]">Updating…</p>}
            <input
              type="text"
              value={ownerSearchTerm}
              onChange={(e) => { setOwnerSearchTerm(e.target.value); searchUsersForOwner(e.target.value); }}
              placeholder="Type 2+ characters to search users..."
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-sm mb-2 disabled:opacity-60"
              autoFocus
              disabled={ownerUpdateInProgress}
            />
            {ownerSearchLoading && <p className="text-sm text-[var(--color-secondary-text)]">Searching…</p>}
            {!ownerSearchLoading && ownerSearchResults.length > 0 && (
              <div className="relative z-[102] mt-2 rounded-md shadow-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)]">
                <ul className="max-h-48 overflow-y-auto divide-y divide-[var(--color-border)]">
                  {ownerSearchResults.map((u: any) => (
                    <li key={u.id}>
                      <button
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (!ownerUpdateInProgress) handleOwnerSelect(u); }}
                        disabled={ownerUpdateInProgress}
                        className="w-full text-left px-3 py-2.5 text-sm disabled:opacity-60 disabled:pointer-events-none bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] hover:bg-[var(--color-hover-bg)] border-0 cursor-pointer"
                      >
                        <span className="font-semibold">{`${u.first_name || u.firstName || ''} ${u.last_name || u.lastName || ''}`.trim() || u.email || u.id}</span>
                        {u.email && <span className="ml-1 text-xs text-[var(--color-secondary-text)]">({u.email})</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Settings / Edit panel: full edit form (same as /transformations/:id/edit) */}
      <ContextualPanel
        title={t('transformation.projectSettings') || 'Project settings'}
        hideHeader
        disableItemHover
        items={[]}
        isLoading={false}
        emptyMessage=""
        isOpen={activePanel === 'settings'}
        onClose={() => setActivePanel(null)}
        customContentWrapperClassName="pt-2"
        customContent={
          id ? (
            <TransformationForm
              transformationId={id}
              mode="panel"
              onSuccess={() => { setActivePanel(null); loadTransformation(); }}
              onCancel={() => setActivePanel(null)}
            />
          ) : null
        }
      />

      {/* Discussion Thread Panel */}
      {activePanel === 'discussions' && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
            onClick={() => {
              setActivePanel(null);
              setShowCreateForm(null);
            }}
          />

          {/* Panel */}
          <div className="fixed right-0 top-0 h-full w-full max-w-md bg-[var(--color-secondary-bg)] shadow-xl z-50 transform transition-transform duration-300 ease-in-out translate-x-0 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-[var(--color-surface-text)]">{t('transformation.discussionsPanelTitle') || 'Discussions & Comments'}</h2>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[var(--color-success)]/20 text-[var(--color-success)]" title={t('transformation.live') || 'Live'}>{t('transformation.live') || 'Live'}</span>
              </div>
              <button
                onClick={() => {
                  setActivePanel(null);
                  setShowCreateForm(null);
                }}
                className="p-1 rounded-lg hover:bg-[var(--color-hover-bg)] text-[var(--color-secondary-text)]"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Thread Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {commentsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent-text)] mx-auto"></div>
                  <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mt-2">Loading...</p>
                </div>
              ) : comments.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-[var(--color-secondary-text)] opacity-75">{t('transformation.noCommentsYet') || 'No comments yet. Start the discussion below!'}</p>
                </div>
              ) : (
                // Sort comments by created_at ascending (oldest first) for thread view
                [...comments].sort((a: any, b: any) => {
                  const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
                  const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
                  return aDate - bDate; // Ascending order (oldest first) for thread
                }).map((comment: any) => {
                  // Check if this comment is from the current user
                  const isCurrentUser = currentUser?.id && (comment.created_by === currentUser.id || comment.user_id === currentUser.id);
                  
                  return (
                    <div
                      key={comment.id}
                      className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-lg ${
                          isCurrentUser
                            ? 'bg-[var(--color-info)] text-white rounded-br-none'
                            : 'bg-[var(--color-primary-bg)] border border-[var(--color-border)] text-[var(--color-surface-text)] rounded-bl-none'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-medium ${isCurrentUser ? 'text-white opacity-90' : 'text-[var(--color-secondary-text)]'}`}>
                            {isCurrentUser ? (t('transformation.you') || 'You') : (comment.author_name || comment.user_name || (t('transformation.unknown') || 'Unknown'))}
                          </span>
                          {comment.comment_type === 'reply' && (
                            <span className={`text-xs ${isCurrentUser ? 'text-white opacity-75' : 'text-[var(--color-secondary-text)] opacity-75'}`}>
                              {t('transformation.replied') || 'replied'}
                            </span>
                          )}
                        </div>
                        <p className={`text-sm whitespace-pre-wrap ${isCurrentUser ? 'text-white' : 'text-[var(--color-surface-text)]'}`}>
                          {comment.content || comment.comment_text}
                        </p>
                        <div className={`text-xs mt-2 ${isCurrentUser ? 'text-white opacity-75' : 'text-[var(--color-secondary-text)] opacity-75'}`}>
                          {comment.created_at ? new Date(comment.created_at).toLocaleString() : ''}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Comment Input - Fixed at Bottom */}
            {!isReadOnly && (
              <div className="border-t border-[var(--color-border)] p-4 bg-[var(--color-secondary-bg)]">
                <div className="space-y-2">
                  <textarea
                    value={createDiscussionForm.content}
                    onChange={(e) => setCreateDiscussionForm({ ...createDiscussionForm, content: e.target.value })}
                    className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] text-sm resize-none"
                    rows={3}
                    placeholder={t('transformation.enterDiscussionContent') || 'Add a comment...'}
                  />
                  <div className="flex justify-end">
                    <Button 
                      onClick={handleCreateDiscussion} 
                      disabled={!createDiscussionForm.content.trim()}
                      size="sm"
                    >
                      {t('transformation.submit') || 'Submit'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Project History Panel */}
      {activePanel === 'history' && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
            onClick={() => setActivePanel(null)}
          />
          <div className="fixed right-0 top-0 h-full w-full max-w-md bg-[var(--color-secondary-bg)] shadow-xl z-50 transform transition-transform duration-300 ease-in-out translate-x-0 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
              <h2 className="text-lg font-semibold text-[var(--color-surface-text)]">Project history</h2>
              <button
                onClick={() => setActivePanel(null)}
                className="p-1 rounded-lg hover:bg-[var(--color-hover-bg)] text-[var(--color-secondary-text)]"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {projectHistoryLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent-text)] mx-auto" />
                  <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mt-2">Loading...</p>
                </div>
              ) : projectHistory.length === 0 ? (
                <p className="text-sm text-[var(--color-secondary-text)] opacity-75 text-center py-8">No activity yet. Changes to inchstones, stakeholders, next steps, and settings will appear here.</p>
              ) : (
                <ul className="space-y-4">
                  {projectHistory.map((entry: any) => {
                    const isStatus = entry.type === 'project_status';
                    const ps = entry.project_status || entry;
                    const hasStatusContent = isStatus && (ps.project_summary || ps.overall_summary || ps.cost_summary || ps.benefits_summary || ps.execution_health_summary || ps.next_steps || ps.asks);
                    const statuses = isStatus ? [ps.overall_status, ps.cost_status, ps.benefits_status, ps.execution_health_status].filter(Boolean) : [];
                    return (
                      <li key={`${entry.type}-${entry.id}`} className="border-l-2 border-[var(--color-border)] pl-4 py-1">
                        <div className="text-xs text-[var(--color-secondary-text)] mb-1">
                          {entry.created_at ? new Date(entry.created_at).toLocaleString() : ''}
                          {entry.created_by_name || entry.created_by_email ? (
                            <span>
                              {' · '}
                              {entry.created_by_email ? (
                                <a
                                  href={`mailto:${entry.created_by_email}`}
                                  className="text-[var(--color-info)] hover:underline"
                                >
                                  {entry.created_by_name || entry.created_by_email}
                                </a>
                              ) : (
                                entry.created_by_name
                              )}
                            </span>
                          ) : null}
                        </div>
                        {entry.type === 'activity' && (
                          <p className="text-sm text-[var(--color-surface-text)]">{entry.description || entry.activity_type}</p>
                        )}
                        {statuses.length > 0 && (
                          <div className="flex gap-2 mb-2">
                            {statuses.map((s: string) => (
                              <span key={s} className={`text-xs font-medium px-1.5 py-0.5 rounded ${s === 'GREEN' ? 'bg-[var(--color-success)]/20 text-[var(--color-success)]' : s === 'YELLOW' ? 'bg-[var(--color-warning)]/20 text-[var(--color-warning)]' : 'bg-[var(--color-danger)]/20 text-[var(--color-danger)]'}`}>{s}</span>
                            ))}
                          </div>
                        )}
                        {hasStatusContent && (
                          <div className="text-sm text-[var(--color-surface-text)] whitespace-pre-wrap space-y-1">
                            {ps.project_summary && <p>{ps.project_summary}</p>}
                            {!ps.project_summary && (
                              <>
                                {ps.overall_summary && <p><span className="text-[var(--color-secondary-text)]">Overall:</span> {ps.overall_summary}</p>}
                                {ps.cost_summary && <p><span className="text-[var(--color-secondary-text)]">Cost:</span> {ps.cost_summary}</p>}
                                {ps.benefits_summary && <p><span className="text-[var(--color-secondary-text)]">Benefits:</span> {ps.benefits_summary}</p>}
                                {ps.execution_health_summary && <p><span className="text-[var(--color-secondary-text)]">Execution:</span> {ps.execution_health_summary}</p>}
                                {ps.next_steps && <p><span className="text-[var(--color-secondary-text)]">Next steps:</span> {ps.next_steps}</p>}
                                {ps.asks && <p><span className="text-[var(--color-secondary-text)]">Asks:</span> {ps.asks}</p>}
                              </>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </>
      )}

      {/* Related Actions Panel */}
      <ContextualPanel
        title="Related Actions"
        items={actions.map((action): ContextualPanelItem => ({
          id: action.id,
          title: action.description || action.name,
          description: `${action.priority} - ${action.status}`,
          status: `${action.priority} - ${action.status}`,
          metadata: action,
          onClick: () => {
            navigate(`/actions/${action.id}`);
            setActivePanel(null);
          }
        }))}
        isLoading={actionsLoading}
        emptyMessage="None"
        onCreateNew={isReadOnly ? undefined : () => setShowCreateForm('actions')}
        onCreateLabel="Create Action"
        showCreateForm={showCreateForm === 'actions'}
        onCancelCreate={() => {
          setShowCreateForm(null);
          setCreateActionForm({ description: '', priority: 'MEDIUM', due_date: '' });
        }}
        renderCreateForm={() => (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                {t('common.description') || 'Description'} *
              </label>
              <textarea
                value={createActionForm.description}
                onChange={(e) => setCreateActionForm({ ...createActionForm, description: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] text-sm"
                rows={3}
                placeholder={t('transformation.enterActionDescription') || 'Enter action description...'}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                  {t('transformation.priority') || 'Priority'}
                </label>
                <select
                  value={createActionForm.priority}
                  onChange={(e) => setCreateActionForm({ ...createActionForm, priority: e.target.value as any })}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] text-sm"
                >
                  <option value="LOW">{t('transformation.low') || 'Low'}</option>
                  <option value="MEDIUM">{t('transformation.medium') || 'Medium'}</option>
                  <option value="HIGH">{t('transformation.high') || 'High'}</option>
                  <option value="CRITICAL">{t('transformation.critical') || 'Critical'}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                  {t('transformation.dueDate') || 'Due Date'}
                </label>
                <input
                  type="date"
                  value={createActionForm.due_date}
                  onChange={(e) => setCreateActionForm({ ...createActionForm, due_date: e.target.value })}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] text-sm"
                />
              </div>
            </div>

            {/* Assign To Users */}
            <div>
              <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                {t('actions.assignTo') || 'Assign To'} <span className="text-red-500">*</span>
              </label>
              
              {/* Assigned Users List */}
              {createActionAssignedUserIds.length > 0 && (
                <div className="mb-2 space-y-1">
                  {createActionAssignedUserIds.map(userId => {
                    const user = createActionAllUsers.find(u => u.id === userId);
                    return user ? (
                      <div key={userId} className="flex items-center justify-between p-2 bg-[var(--color-surface-bg)] rounded text-sm text-[var(--color-surface-text)] border border-[var(--color-border)]">
                        <span>{user.name} {user.email && <span className="text-[var(--color-secondary-text)]">({user.email})</span>}</span>
                        <button
                          type="button"
                          onClick={() => handleUnassignActionUser(userId)}
                          className="text-[var(--color-error)] hover:opacity-80"
                          title={t('actions.unassign') || 'Unassign'}
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ) : null;
                  })}
                </div>
              )}

              {/* User Search */}
              <div ref={createActionUserSearchRef} className="relative">
                <button
                  type="button"
                  onClick={() => setShowCreateActionUserSearch(!showCreateActionUserSearch)}
                  className="w-full flex items-center justify-between px-3 py-2 border border-[var(--color-border)] rounded-md text-sm text-left bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] hover:bg-[var(--color-hover-bg)]"
                >
                  <span className="text-[var(--color-secondary-text)]">{t('actions.searchUsers') || 'Search users to assign...'}</span>
                  <PlusIcon className="w-4 h-4 text-[var(--color-secondary-text)]" />
                </button>
                
                {showCreateActionUserSearch && (
                  <div className="absolute z-10 w-full mt-1 border border-[var(--color-border)] rounded-md shadow-lg bg-[var(--color-surface-bg)]">
                    <input
                      type="text"
                      value={createActionUserSearchTerm}
                      onChange={(e) => setCreateActionUserSearchTerm(e.target.value)}
                      placeholder={t('actions.searchUsers') || 'Search by name or email...'}
                      className="w-full px-3 py-2 border-b border-[var(--color-border)] rounded-t-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]"
                      autoFocus
                    />
                    {createActionUserSearchLoading && (
                      <div className="px-3 py-2 text-sm text-[var(--color-secondary-text)]">
                        {t('actions.loading') || 'Loading...'}
                      </div>
                    )}
                    {!createActionUserSearchLoading && createActionUserSearchResults.length > 0 && (
                      <div className="max-h-60 overflow-y-auto">
                        {createActionUserSearchResults.map((user) => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => handleAssignActionUser(user.id)}
                            className="w-full text-left px-3 py-2 text-sm bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] hover:bg-[var(--color-hover-bg)]"
                          >
                            <div className="font-medium">{user.name}</div>
                            {user.email && (
                              <div className="text-xs text-[var(--color-secondary-text)]">{user.email}</div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                    {!createActionUserSearchLoading && createActionUserSearchTerm && createActionUserSearchResults.length === 0 && (
                      <div className="px-3 py-2 text-sm text-[var(--color-secondary-text)]">
                        {t('actions.noUsersFound') || 'No users found'}
                      </div>
                    )}
                    {!createActionUserSearchLoading && !createActionUserSearchTerm && createActionUserSearchResults.length === 0 && createActionAllUsers.length > 0 && (
                      <div className="px-3 py-2 text-sm text-[var(--color-secondary-text)]">
                        {t('actions.typeToSearch') || 'Type to search users...'}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {createActionAssignedUserIds.length === 0 && (
                <div className="text-xs text-amber-600 mt-1">
                  {t('actions.assignToWarning') || 'Please assign this action to at least one user'}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreateAction} className="flex-1">
                {t('transformation.create') || 'Create'}
              </Button>
              <Button variant="ghost" onClick={() => {
                setShowCreateForm(null);
                setCreateActionForm({ description: '', priority: 'MEDIUM', due_date: '' });
                setCreateActionAssignedUserIds([]);
                setCreateActionUserSearchTerm('');
                setCreateActionUserSearchResults([]);
                setShowCreateActionUserSearch(false);
              }} className="flex-1">
                {t('common.cancel') || 'Cancel'}
              </Button>
            </div>
          </div>
        )}
        isOpen={activePanel === 'actions'}
        onClose={() => {
          setActivePanel(null);
          setShowCreateForm(null);
        }}
      />

    </>
  );
};

export default TransformationDetailPage;

// InchstoneHierarchy and InchstoneRow components are now imported from shared component
// See: @/components/inchstones/InchstoneHierarchy 